import { useState, useEffect } from "react";
import { db, auth } from "../firebase/config"; // abc-dashboard's own DB — all draft writes go here
import { signInAnonymously, signInWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc, collection, query, where, getDocs, writeBatch, getDoc, deleteField } from "firebase/firestore";
// import { saasDb, saasAuth, createSaasAuthAccount, generateRandomPassword, generateTempPassword, buildFlatInternalEmail } from "./saasAppConfig"; // saas-app's DB — only touched by the final push
// import { sendPasswordResetEmail } from "firebase/auth";
import bcrypt from "bcryptjs";
import * as XLSX from "xlsx";
import adyantamLogo from "../assets/adyantam-logo.png";
import termsAndConditionsPdf from "../assets/terms-and-conditions.pdf";
import { ResumeSetup } from "./ResumeSetup";
import { fmtD } from "../crm/dateFormat";

// SECURITY FIX (see Security & Launch-Readiness Audit, Finding 6):
// admin@adyantam.in's password used to be read from this REACT_APP_
// variable, which meant it shipped inside the public browser bundle —
// anyone who loaded abc-dashboard could pull it out. That whole
// approach is retired below in favor of a Cloud Function
// (provisionSociety) gated by a staff-only custom claim. Left here,
// commented, only as a historical reference — do not uncomment.
// const SAAS_ADMIN_EMAIL = "admin@adyantam.in";
// const SAAS_ADMIN_PASSWORD = process.env.REACT_APP_SAAS_ADMIN_PASSWORD;

import { getFunctions, httpsCallable } from "firebase/functions";
import app from "../firebase/config"; // abc-dashboard's own Firebase app instance (default export)

const functions = getFunctions(app, "asia-south1");
const provisionSocietyFn = httpsCallable(functions, "provisionSociety");

// The mailbox app password is sent ONLY to this function in the main app
// (abc-saas-prod), which locks it in a vault. The CRM never saves it.
// The web address below must be checked when the function is deployed.
const SAVE_MAILBOX_LOGIN_URL =
  "https://asia-south1-abc-saas-prod.cloudfunctions.net/saveMailboxLogin";

async function saveMailboxLoginToVault(details) {
  const user = auth.currentUser;
  if (!user) throw new Error("You are not signed in. Please sign in again.");
  const idToken = await user.getIdToken();
  let res;
  try {
    res = await fetch(SAVE_MAILBOX_LOGIN_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + idToken,
      },
      body: JSON.stringify(details),
    });
  } catch (e) {
    throw new Error("Could not reach the server. Check your connection.");
  }
  let body = null;
  try {
    body = await res.json();
  } catch (e) {
    body = null;
  }
  if (!res.ok || !body || body.success !== true) {
    throw new Error((body && body.error) || "Could not save the mailbox password.");
  }
}

// Normalizes free-text floor entries (from Excel upload or manual entry)
// into the exact strings FlatsScreen groups by ("Ground", "1st Floor",
// "2nd Floor", "3rd Floor") — without this, a value like "2nd" (instead of
// "2nd Floor") silently drops that flat from the grouped display, even
// though it's correctly saved and counted elsewhere. Module-level so both
// Step2 (Excel upload) and the main SetupWizard component (manual entry
// save) can use it.
// Mobile numbers: an Indian number is saved as plain 10 digits; a US number is saved as +1 and 10 digits
// (the same shapes the main app sign-in uses). Nothing is cut silently: a number that fits neither shape
// is kept as typed and fails the check with a clear message.
const MOBILE_OK = /^(\d{10}|\+1\d{10})$/;
function isValidMobile(m) { return MOBILE_OK.test(m || ""); }
function cleanMobileTyping(raw) {
  const s = String(raw ?? "").replace(/[^\d+]/g, "");
  return (s.startsWith("+") ? "+" : "") + s.replace(/\D/g, "");
}
function normalizeMobile(raw) {
  const clean = cleanMobileTyping(raw);
  const digits = clean.replace(/\D/g, "");
  if (digits.length === 11 && digits[0] === "1") return "+" + digits; // US number typed with or without the +
  return digits;
}

function normalizeFloor(raw) {
  const s = String(raw ?? "").trim().toLowerCase();
  if (!s) return "";
  if (/^(ground|g|gf|0)$/.test(s)) return "Ground";
  if (/^(1|1st|first|1st floor)$/.test(s)) return "1st Floor";
  if (/^(2|2nd|second|2nd floor)$/.test(s)) return "2nd Floor";
  if (/^(3|3rd|third|3rd floor)$/.test(s)) return "3rd Floor";
  return null; // unrecognized — caller should treat as a validation error
}

// abc-dashboard's own anon-auth helper (unrelated to the saas-app push below).
async function ensureLocalAnonAuth() {
  if (auth.currentUser) return auth.currentUser.uid;
  const cred = await signInAnonymously(auth);
  return cred.user.uid;
}

// SECURITY FIX (Finding 6): this whole function used to sign into the
// saas-app as admin@adyantam.in using the exposed password above.
// Commented out, kept only for reference — replaced by the
// provisionSociety Cloud Function call further below.
//
// async function ensureSaasAuthAllowlisted() {
//   if (!saasAuth.currentUser) {
//     await signInWithEmailAndPassword(saasAuth, SAAS_ADMIN_EMAIL, SAAS_ADMIN_PASSWORD);
//   }
//   return saasAuth.currentUser.uid;
// }
//
// // Reads the finished draft out of abc-dashboard's own DB and writes it,
// // as a batch, into saas-app's DB. Runs once, at Confirm. Anything before
// // this point never touches saas-app at all.
// //
// // CHANGED: this used to just copy the draft's users/{uid} docs straight
// // across, carrying the OLD bcrypt-password scheme and abc-dashboard's
// // own random doc IDs. Now it creates a REAL Firebase Auth account in
// // saas-app for every member (via createSaasAuthAccount, the throwaway-
// // connection trick — no Cloud Functions needed) and uses THAT account's
// // real uid as the doc ID going forward. Members get a "set your
// // password" email immediately, or a temp password for manual handoff if
// // no real email is on file. Watchman is NOT set up here — that account
// // is created later by the President from within saas-app itself.
// async function pushSocietyToSaasApp(societyId, onProgress) {
//   await ensureSaasAuthAllowlisted();
//
//   const societySnap = await getDoc(doc(db, "societies", societyId));
//   const settingsSnap = await getDoc(doc(db, "societies", societyId, "settings", "init"));
//   const flatsSnap = await getDocs(collection(db, "societies", societyId, "flats"));
//   const membersSnap = await getDocs(collection(db, "societies", societyId, "members"));
//
//   // Pull each member's linked users/{uid} doc too (President + owners).
//   const userDocs = {};
//   for (const m of membersSnap.docs) {
//     const uSnap = await getDoc(doc(db, "users", m.id));
//     if (uSnap.exists()) userDocs[m.id] = uSnap.data();
//   }
//
//   // ── Create a real saas-app account for every member first ────────
//   // Sequential on purpose — keeps the throwaway-connection cleanup
//   // simple and avoids hammering Firebase Auth with parallel requests.
//   const failures = [];
//   const manualHandoffs = []; // members with no real email — need their temp password relayed by hand
//   const newUidByOldId = {};
//   const memberDocs = membersSnap.docs;
//
//   for (let i = 0; i < memberDocs.length; i++) {
//     const m = memberDocs[i];
//     const memberData = m.data();
//     const baseData = userDocs[m.id] || {};
//     const realEmailOnFile = (memberData.emailId || baseData.emailId || "").trim();
//     // No real email on file — falls back to a flat-based internal address
//     // instead of being skipped. NOTE: if two members share the same flat
//     // and BOTH have no email, this will collide (second one fails account
//     // creation) — fine for now, flag it if it comes up in the failures list.
//     const usesInternalEmail = !realEmailOnFile;
//
//     onProgress?.(`Creating account ${i + 1} of ${memberDocs.length}: ${memberData.name || m.id}…`);
//
//     let authEmail;
//     let password;
//     if (usesInternalEmail) {
//       const flatId = (memberData.flatIds && memberData.flatIds[0]) || m.id;
//       authEmail = buildFlatInternalEmail(flatId, societyId);
//       password = generateTempPassword(); // shown to admin below, for manual handoff
//     } else {
//       authEmail = realEmailOnFile;
//       password = generateRandomPassword(); // temporary — reset email sent right after
//     }
//
//     const created = await createSaasAuthAccount(authEmail, password);
//     if (!created.success) {
//       failures.push(`${memberData.name || m.id} (${authEmail}): ${created.error}`);
//       continue;
//     }
//
//     newUidByOldId[m.id] = created.uid;
//
//     if (!usesInternalEmail) {
//       // Regular members with a real email set their own password via
//       // Firebase's own email — the random one above is never used or shown.
//       try {
//         await sendPasswordResetEmail(saasAuth, authEmail);
//       } catch (err) {
//         failures.push(`${memberData.name || m.id}: account created, but reset email failed to send`);
//       }
//     }
//
//     if (usesInternalEmail) {
//       manualHandoffs.push(`${memberData.name || m.id}: temporary password ${password} (hand this to them directly — no email on file)`);
//     }
//
//     const userDoc = {
//       mobile: memberData.mobile || baseData.mobile || "",
//       name: memberData.name || baseData.name || "",
//       authEmail,
//       realEmail: usesInternalEmail ? null : authEmail,
//       societies: [societyId],
//       active: true,
//       mustChangePassword: usesInternalEmail, // forces them to set their own password on first login
//     };
//     await setDoc(doc(saasDb, "users", created.uid), userDoc);
//   }
//
//   // ── Now copy the rest, using the new real uids throughout ────────
//   let batch = writeBatch(saasDb);
//   let ops = 0;
//   const commitIfFull = async () => {
//     if (ops >= 400) { await batch.commit(); batch = writeBatch(saasDb); ops = 0; }
//   };
//
//   batch.set(doc(saasDb, "societies", societyId), societySnap.data());
//   ops++;
//   await commitIfFull();
//
//   if (settingsSnap.exists()) {
//     batch.set(doc(saasDb, "societies", societyId, "settings", "init"), {
//       ...settingsSnap.data(),
//       setupComplete: true,
//     });
//     ops++;
//     await commitIfFull();
//   }
//
//   for (const f of flatsSnap.docs) {
//     batch.set(doc(saasDb, "societies", societyId, "flats", f.id), f.data());
//     ops++;
//     await commitIfFull();
//   }
//
//   for (const m of membersSnap.docs) {
//     const newUid = newUidByOldId[m.id];
//     if (!newUid) continue; // account creation failed for this member — see failures list
//     batch.set(doc(saasDb, "societies", societyId, "members", newUid), m.data());
//     ops++;
//     await commitIfFull();
//   }
//
//   if (ops > 0) await batch.commit();
//
//   return { failures, manualHandoffs };
// }

// SECURITY FIX (Finding 6): replaces everything commented out above.
// Instead of signing into saas-app as a shared admin account, this
// calls a Cloud Function that runs the same logic server-side, gated
// by the caller's own "staff" custom claim (see grant-staff-claim.js).
// Kept the same name/shape (societyId, onProgress) so nothing else in
// this file needs to change — callers still read .failures and
// .manualHandoffs from the result; .resetEmailFailures is new.
async function pushSocietyToSaasApp(societyId, onProgress) {
  onProgress?.("Provisioning society in saas-app…");
  const result = await provisionSocietyFn({ societyId });
  return result.data; // { failures, manualHandoffs, resetEmailFailures }
}

// ── Styles ─────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: "100vh", background: "#0f0f13",
    display: "flex", flexDirection: "column", alignItems: "center",
    padding: "24px 16px", fontFamily: "'Segoe UI', Arial, sans-serif",
  },
  brand: { textAlign: "center", marginBottom: "20px" },
  logo: { width: "56px", height: "56px", borderRadius: "50%", objectFit: "cover", marginBottom: "6px" },
  brandName: { fontSize: "20px", fontWeight: "700", color: "#6c63ff", letterSpacing: "2px" },
  brandSub: { fontSize: "14px", color: "#8888a8", marginTop: "4px", fontWeight: "500" },

  // Progress bar
  progressWrap: { width: "100%", maxWidth: "480px", marginBottom: "20px" },
  progressTrack: { height: "4px", background: "#2e2e40", borderRadius: "2px", marginBottom: "8px" },
  progressFill: (pct) => ({
    height: "100%", background: "#6c63ff", borderRadius: "2px",
    width: `${pct}%`, transition: "width 0.3s ease",
  }),
  stepLabel: { fontSize: "11px", color: "#8888a8", textAlign: "center" },

  card: {
    background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "16px",
    padding: "24px", width: "100%", maxWidth: "480px",
  },
  cardTitle: { fontSize: "16px", fontWeight: "700", color: "#e8e8f0", marginBottom: "4px" },
  cardSub: { fontSize: "12px", color: "#555570", marginBottom: "20px" },

  field: { marginBottom: "14px" },
  label: { display: "block", fontSize: "12px", color: "#8888a8", marginBottom: "5px" },
  input: {
    width: "100%", background: "#0f0f13", border: "1px solid #2e2e40",
    borderRadius: "10px", padding: "11px 14px", color: "#e8e8f0",
    fontSize: "14px", outline: "none", boxSizing: "border-box",
  },
  select: {
    width: "100%", background: "#0f0f13", border: "1px solid #2e2e40",
    borderRadius: "10px", padding: "11px 14px", color: "#e8e8f0",
    fontSize: "14px", outline: "none", boxSizing: "border-box",
  },
  row: { display: "flex", gap: "10px" },

  // Flat table
  flatRow: {
    display: "grid", gridTemplateColumns: "60px 1fr 100px 1.2fr 70px 90px 80px",
    gap: "6px", marginBottom: "8px", alignItems: "center",
  },
  flatHeader: {
    display: "grid", gridTemplateColumns: "60px 1fr 100px 1.2fr 70px 90px 80px",
    gap: "6px", marginBottom: "6px",
  },
  flatHeaderCell: { fontSize: "10px", color: "#555570", textTransform: "uppercase", letterSpacing: "0.4px" },
  flatInput: {
    background: "#0f0f13", border: "1px solid #2e2e40", borderRadius: "8px",
    padding: "8px 10px", color: "#e8e8f0", fontSize: "13px",
    outline: "none", width: "100%", boxSizing: "border-box",
  },
  flatSelect: {
    background: "#0f0f13", border: "1px solid #2e2e40", borderRadius: "8px",
    padding: "8px 6px", color: "#e8e8f0", fontSize: "12px",
    outline: "none", width: "100%", boxSizing: "border-box",
  },

  // Buttons
  btnPrimary: {
    width: "100%", background: "#6c63ff", color: "#fff", border: "none",
    borderRadius: "10px", padding: "13px", fontSize: "15px", fontWeight: "600",
    cursor: "pointer", marginTop: "8px",
  },
  btnSecondary: {
    width: "100%", background: "transparent", color: "#8888a8",
    border: "1px solid #2e2e40", borderRadius: "10px", padding: "11px",
    fontSize: "14px", cursor: "pointer", marginTop: "8px",
  },
  btnRow: { display: "flex", gap: "10px", marginTop: "8px" },

  error: {
    background: "#ff525215", border: "1px solid #ff525240", borderRadius: "10px",
    padding: "10px 14px", fontSize: "13px", color: "#ff5252", marginBottom: "12px",
  },

  // Review
  reviewSection: { marginBottom: "16px" },
  reviewTitle: {
    fontSize: "11px", fontWeight: "600", color: "#8888a8",
    textTransform: "uppercase", letterSpacing: "0.5px",
    borderBottom: "1px solid #2e2e40", paddingBottom: "6px", marginBottom: "10px",
  },
  reviewRow: { display: "flex", justifyContent: "space-between", marginBottom: "6px" },
  reviewKey: { fontSize: "12px", color: "#555570" },
  reviewVal: { fontSize: "12px", color: "#e8e8f0", fontWeight: "500", textAlign: "right", maxWidth: "60%" },
};

const STEPS = ["Identity", "Billing", "Reconciliation", "Bank Email", "Flats", "Pricing", "Review"];
const TRIAL_DAYS = 45;

// Stage 1 — Welcome tour (6 themes, forward-only, no Skip). Runs once,
// before Stage 5 (the technical steps below), per the design doc.
const WELCOME_THEMES = [
  { mono: "💰", short: "Money", title: "Money, made transparent", description: "No more Excel sheets or WhatsApp chaos. Dues, payments, and every rupee spent, tracked automatically and visible to everyone." },
  { mono: "💧", short: "Water", title: "Water management", description: "No more manual meter readings. Track water and tanker billing automatically." },
  { mono: "🏠", short: "Flats", title: "Flats and tenants", description: "Flat records, ownership transfers, and tenant details — all in one place, always up to date." },
  { mono: "🛠️", short: "Tasks", title: "Tasks and events", description: "Repairs and society projects tracked properly. No more requests lost in phone calls." },
  { mono: "🗳️", short: "Governance", title: "Governance, digitized", description: "Bylaws, polls, and elections — handled digitally, with a clear record every time." },
  { mono: "💬", short: "Connect", title: "Stay connected", description: "Chat and important contacts, built right into the app. No more digging through old messages." },
];

// Stage 3 — Feature selection. Per the design doc's mandatory/optional
// table: Maintenance/Passbook, Flats & tenants, Connect, and Feedback
// are always on; Water, Tasks, and Governance are optional with a nudge
// pop-up when switched off.
const FEATURES = [
  { key: "maintenance", icon: "💰", title: "Maintenance and Passbook", mandatory: true,
    description: "Dues, payments, and a full passbook — always visible to everyone." },
  { key: "flats", icon: "🏠", title: "Flats and tenants", mandatory: true,
    description: "Flat records, ownership transfers, and tenant details, always kept up to date." },
  { key: "water", icon: "💧", title: "Water management", mandatory: false,
    description: "Track water and tanker billing without the guesswork.",
    nudge: "Many societies use this to track water and tanker billing accurately instead of guessing. Are you sure you want to skip it?" },
  { key: "tasks", icon: "🛠️", title: "Tasks and events", mandatory: false,
    description: "Tired of repair requests getting lost in phone calls? Track them properly here.",
    nudge: "Many societies use this to track repairs easily instead of using WhatsApp. Are you sure you want to skip it?" },
  { key: "governance", icon: "🗳️", title: "Governance (Elections/Polls)", mandatory: false,
    description: "Bylaws, polls, and elections — handled digitally, with a clear record every time.",
    nudge: "Many societies use this to run polls and elections fairly and transparently. Are you sure you want to skip it?" },
  { key: "connect", icon: "💬", title: "Connect (Notifications)", mandatory: true,
    description: "Chat and important contacts, always within reach." },
  { key: "feedback", icon: "⭐", title: "Feedback", mandatory: true,
    description: "Residents can share feedback on the app, right from inside it." },
];

// Generates the actual Firestore document ID for a new society, in the
// format "ABC-XXXXXX" — a fixed "ABC" brand prefix plus 6 random
// characters — instead of leaving it to Firebase's own random auto-ID.
// This is the ONLY ID a society has: used as the real database key AND
// as the code given to the President for support calls/lookups. Checked
// for uniqueness BEFORE the society document is created. This ID
// becomes permanent and is used throughout the app to link every
// record (flats, members, payments...) to this one society — never
// changed once assigned.
const SOCIETY_ID_PREFIX = "ABC";
async function generateUniqueSocietyId() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // no 0/O/1/I to avoid confusion
  for (let attempt = 0; attempt < 10; attempt++) {
    let suffix = "";
    for (let i = 0; i < 6; i++) suffix += chars[Math.floor(Math.random() * chars.length)];
    const candidateId = `${SOCIETY_ID_PREFIX}-${suffix}`;
    const existing = await getDoc(doc(db, "societies", candidateId));
    if (!existing.exists()) return candidateId;
  }
  throw new Error("Could not generate a unique society ID. Please try again.");
}

// ── Stage 1: Welcome tour ────────────────────────────────────────
function WelcomeTour({ screen, setScreen, onFinish, onResumeClick }) {
  const isFirst = screen === 0;
  const isLast = screen === WELCOME_THEMES.length - 1;
  const theme = WELCOME_THEMES[screen];

  return (
    <>
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "center", gap: "6px", marginBottom: "24px" }}>
          {WELCOME_THEMES.map((_, i) => (
            <div key={i} style={{
              width: i === screen ? "22px" : "6px", height: "6px", borderRadius: "3px",
              background: i === screen ? "#6c63ff" : "#2e2e40", transition: "all 0.2s ease",
            }} />
          ))}
        </div>

        <div style={{
          width: "72px", height: "72px", borderRadius: "50%",
          background: "#6c63ff22", display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 20px",
        }}>
          <span style={{ fontSize: "32px" }}>{theme.mono}</span>
        </div>

        <div style={{ ...S.cardTitle, textAlign: "center", fontSize: "18px" }}>{theme.title}</div>
        <div style={{ ...S.cardSub, textAlign: "center" }}>{theme.description}</div>

        <div style={S.btnRow}>
          {!isFirst && (
            <button style={{ ...S.btnSecondary, width: "auto", flex: 1, marginTop: 0 }} onClick={() => setScreen(screen - 1)}>← Back</button>
          )}
          <button style={{ ...S.btnPrimary, width: "auto", marginTop: 0, flex: isFirst ? 1 : 2 }}
            onClick={() => (isLast ? onFinish() : setScreen(screen + 1))}>
            {isLast ? "Let's get started" : "Next →"}
          </button>
        </div>

        <div style={{ textAlign: "center", marginTop: "14px" }}>
          <span style={{ fontSize: "12px", color: "#6c63ff", cursor: "pointer", textDecoration: "underline" }} onClick={onResumeClick}>
            Already started? Resume with your Setup ID
          </span>
        </div>
      </div>

      <div style={{ marginTop: "20px", textAlign: "center", maxWidth: "480px", width: "100%" }}>
        <div style={{ fontSize: "11px", color: "#8888a8", marginBottom: "12px" }}>Preview — the other five screens in this tour</div>
        <div style={{ display: "flex", justifyContent: "center", gap: "18px", flexWrap: "wrap" }}>
          {WELCOME_THEMES.filter((_, i) => i !== screen).map((t, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
              <div style={{
                width: "36px", height: "36px", borderRadius: "50%",
                border: "1px solid #2e2e40", background: "#1a1a24",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px",
              }}>
                {t.mono}
              </div>
              <span style={{ fontSize: "10px", color: "#8888a8" }}>{t.short}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function Progress({ step, globalStep, totalScreens }) {
  return (
    <div style={S.progressWrap}>
      <div style={S.progressTrack}>
        <div style={S.progressFill(((step + 1) / STEPS.length) * 100)} />
      </div>
      <div style={S.stepLabel}>Step {globalStep} of {totalScreens} — {STEPS[step]}</div>
    </div>
  );
}

// ── Stage 2: Customer Introduction ───────────────────────────────
function CustomerIntroInfo({ onNext, onBack }) {
  return (
    <div style={S.card}>
      <div style={{ fontSize: "40px", textAlign: "center", marginBottom: "10px" }}>👋</div>
      <div style={{ ...S.cardTitle, textAlign: "center", fontSize: "20px" }}>Hey, we're Adyantam</div>
      <div style={{ ...S.cardSub, textAlign: "center", fontSize: "13px" }}>Before we get your details — a quick word about us.</div>

      <div style={{
        background: "linear-gradient(135deg, #6c63ff22, #6c63ff08)",
        border: "1px solid #6c63ff40", borderRadius: "14px",
        padding: "20px", marginBottom: "20px", textAlign: "center",
      }}>
        <div style={{ fontSize: "15px", color: "#e8e8f0", lineHeight: "1.8", fontWeight: "500" }}>
          One app for your whole society — <span style={{ color: "#a29bff" }}>maintenance</span>,{" "}
          <span style={{ color: "#a29bff" }}>water</span>, <span style={{ color: "#a29bff" }}>flats</span>,{" "}
          <span style={{ color: "#a29bff" }}>tasks</span>, and <span style={{ color: "#a29bff" }}>governance</span> — no more chasing WhatsApp groups.
        </div>
        <div style={{ height: "1px", background: "#6c63ff30", margin: "16px 0" }} />
        <div style={{ fontSize: "13px", color: "#c8c8e0" }}>📞 7337349396 &nbsp;·&nbsp; ✉️ support@adyantam.in</div>
      </div>

      <div style={S.btnRow}>
        <button style={{ ...S.btnSecondary, width: "auto", flex: 1, marginTop: 0 }} onClick={onBack}>← Back</button>
        <button style={{ ...S.btnPrimary, width: "auto", marginTop: 0, flex: 2 }} onClick={onNext}>Next →</button>
      </div>
    </div>
  );
}

function McSizeScreen({ mcMemberCount, onMcCountChange, onNext, onBack, saving }) {
  return (
    <div style={S.card}>
      <div style={S.cardTitle}>Your Managing Committee</div>
      <div style={S.cardSub}>How many members run the society? This decides how many MC Role slots (President / Secretary / Treasurer) your flats upload must fill later.</div>

      <div style={S.field}>
        <label style={S.label}>Managing Committee Size</label>
        <div style={{ display: "flex", gap: "8px" }}>
          {["1", "2", "3"].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onMcCountChange(n)}
              style={{
                flex: 1, padding: "10px 8px", borderRadius: "8px", fontSize: "12px", fontWeight: "600",
                border: mcMemberCount === n ? "1px solid #6c63ff" : "1px solid #2e2e40",
                background: mcMemberCount === n ? "#6c63ff22" : "transparent",
                color: mcMemberCount === n ? "#a29bff" : "#8888a8", cursor: "pointer",
              }}
            >
              {n} {n === "1" ? "Member" : "Members"}
            </button>
          ))}
        </div>
      </div>

      <div style={S.btnRow}>
        <button style={{ ...S.btnSecondary, width: "auto", flex: 1, marginTop: 0 }} onClick={onBack} disabled={saving}>← Back</button>
        <button style={{ ...S.btnPrimary, width: "auto", marginTop: 0, flex: 2, opacity: saving ? 0.7 : 1, cursor: saving ? "not-allowed" : "pointer" }}
          onClick={onNext} disabled={saving}>
          {saving ? "Saving…" : "Save & Next →"}
        </button>
      </div>
    </div>
  );
}

// ── Stage 3: Feature Selection ───────────────────────────────────
function FeatureSelectionMandatory({ onNext, onBack }) {
  const mandatory = FEATURES.filter((f) => f.mandatory);
  return (
    <div style={S.card}>
      <div style={S.cardTitle}>What's already included</div>
      <div style={S.cardSub}>These are part of every society, no toggle needed.</div>

      <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "18px" }}>
        {mandatory.map((f) => (
          <div key={f.key} style={{
            display: "flex", alignItems: "center", gap: "12px",
            background: "#0f0f13", border: "1px solid #2e2e40", borderRadius: "12px", padding: "12px 14px",
          }}>
            <div style={{
              width: "40px", height: "40px", borderRadius: "50%", flexShrink: 0,
              background: "#6c63ff22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px",
            }}>
              {f.icon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: "14px", fontWeight: "600", color: "#e8e8f0" }}>{f.title}</div>
              <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "2px" }}>{f.description}</div>
            </div>
            <div style={{
              fontSize: "10px", fontWeight: "600", color: "#6c63ff", background: "#6c63ff22",
              borderRadius: "999px", padding: "4px 10px", flexShrink: 0, whiteSpace: "nowrap",
            }}>
              Included
            </div>
          </div>
        ))}
      </div>

      <div style={S.btnRow}>
        <button style={{ ...S.btnSecondary, width: "auto", flex: 1, marginTop: 0 }} onClick={onBack}>← Back</button>
        <button style={{ ...S.btnPrimary, width: "auto", marginTop: 0, flex: 2 }} onClick={onNext}>Next →</button>
      </div>
    </div>
  );
}

function FeatureSelectionOptional({ enabled, onToggle, waterMode, onWaterMode, waterApprovalFlow, onWaterApprovalFlow, onNext, onBack }) {
  const [nudgeFor, setNudgeFor] = useState(null); // feature key currently showing its nudge pop-up
  const optional = FEATURES.filter((f) => !f.mandatory);

  function handleToggleOff(key) {
    onToggle(key, false);
    setNudgeFor(key);
  }

  function turnBackOn() {
    onToggle(nudgeFor, true);
    setNudgeFor(null);
  }

  const nudgeFeature = FEATURES.find((f) => f.key === nudgeFor);

  return (
    <div style={S.card}>
      <div style={S.cardTitle}>What does your society need?</div>
      <div style={S.cardSub}>These are optional — switch off anything you don't need.</div>

      <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "18px" }}>
        {optional.map((f) => (
          <div key={f.key}>
            <div style={{
              display: "flex", alignItems: "center", gap: "12px",
              background: "#0f0f13", border: "1px solid #2e2e40", borderRadius: "12px", padding: "12px 14px",
            }}>
              <div style={{
                width: "40px", height: "40px", borderRadius: "50%", flexShrink: 0,
                background: "#6c63ff22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px",
              }}>
                {f.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: "14px", fontWeight: "600", color: "#e8e8f0" }}>{f.title}</div>
                <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "2px" }}>{f.description}</div>
              </div>
              <label style={{ position: "relative", display: "inline-block", width: "42px", height: "24px", flexShrink: 0 }}>
                <input
                  type="checkbox"
                  checked={enabled[f.key]}
                  onChange={(e) => (e.target.checked ? onToggle(f.key, true) : handleToggleOff(f.key))}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: "absolute", inset: 0, borderRadius: "999px", cursor: "pointer",
                  background: enabled[f.key] ? "#6c63ff" : "#2e2e40", transition: "background 0.15s ease",
                }}>
                  <span style={{
                    position: "absolute", top: "3px", left: enabled[f.key] ? "21px" : "3px",
                    width: "18px", height: "18px", borderRadius: "50%", background: "#fff", transition: "left 0.15s ease",
                  }} />
                </span>
              </label>
            </div>

            {f.key === "water" && enabled.water && (
              <div style={{ marginTop: "8px", marginLeft: "52px" }}>
                <div style={{ display: "flex", gap: "8px" }}>
                  {[["meter", "Meter reading"], ["equal", "Shared equally"]].map(([val, label]) => (
                    <button
                      key={val}
                      onClick={() => onWaterMode(val)}
                      style={{
                        flex: 1, padding: "8px 10px", borderRadius: "8px", fontSize: "12px", fontWeight: "600",
                        border: waterMode === val ? "1px solid #6c63ff" : "1px solid #2e2e40",
                        background: waterMode === val ? "#6c63ff22" : "transparent",
                        color: waterMode === val ? "#a29bff" : "#8888a8", cursor: "pointer",
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "14px", marginBottom: "8px", fontWeight: "600" }}>
                  Water Bill Approval Flow
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {[
                    { value: "auto", label: "Auto", desc: "Bills visible to owners immediately after readings are saved. No approval needed." },
                    { value: "single", label: "Single Step", desc: "President reviews calculated bills and approves & locks. One approval." },
                    { value: "two-step", label: "Two Step (Recommended)", desc: "Secretary / Treasurer confirms readings first, then President approves & locks." },
                  ].map(opt => (
                    <div key={opt.value}
                      onClick={() => onWaterApprovalFlow(opt.value)}
                      style={{
                        padding: "8px 12px", borderRadius: "8px", cursor: "pointer",
                        border: `1px solid ${waterApprovalFlow === opt.value ? "#6c63ff" : "#2e2e40"}`,
                        background: waterApprovalFlow === opt.value ? "#6c63ff18" : "transparent",
                      }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div style={{
                          width: "12px", height: "12px", borderRadius: "50%", flexShrink: 0,
                          border: `2px solid ${waterApprovalFlow === opt.value ? "#6c63ff" : "#555570"}`,
                          background: waterApprovalFlow === opt.value ? "#6c63ff" : "transparent",
                        }} />
                        <div style={{ fontSize: "12px", fontWeight: "600", color: waterApprovalFlow === opt.value ? "#6c63ff" : "#e8e8f0" }}>
                          {opt.label}
                        </div>
                      </div>
                      <div style={{ fontSize: "10px", color: "#8888a8", marginTop: "3px", marginLeft: "20px" }}>
                        {opt.desc}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={S.btnRow}>
        <button style={{ ...S.btnSecondary, width: "auto", flex: 1, marginTop: 0 }} onClick={onBack}>← Back</button>
        <button style={{ ...S.btnPrimary, width: "auto", marginTop: 0, flex: 2 }} onClick={onNext}>Next →</button>
      </div>

      {nudgeFeature && (
        <div style={{
          position: "fixed", inset: 0, background: "#000000aa", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 50, padding: "20px",
        }}>
          <div style={{
            background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "16px",
            padding: "24px", maxWidth: "360px", width: "100%", textAlign: "center",
          }}>
            <div style={{
              width: "56px", height: "56px", borderRadius: "50%", margin: "0 auto 14px",
              background: "#6c63ff22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "26px",
            }}>
              {nudgeFeature.icon}
            </div>
            <div style={{ fontSize: "14px", color: "#e8e8f0", lineHeight: "1.6", marginBottom: "20px" }}>
              {nudgeFeature.nudge}
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button style={{ ...S.btnSecondary, width: "auto", flex: 1, marginTop: 0 }} onClick={() => setNudgeFor(null)}>No, skip it</button>
              <button style={{ ...S.btnPrimary, width: "auto", flex: 1, marginTop: 0 }} onClick={turnBackOn}>Turn it on</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Step 1: Society Details ─────────────────────────────────────
function Step1Identity({ data, onChange, onNext, onBack, saving }) {
  const [error, setError] = useState("");

  function validate() {
    if (!data.societyName.trim()) return "Enter society name.";
    if (!data.location.trim()) return "Enter location.";
    if (!data.city.trim()) return "Enter city.";
    if (!data.pincode.trim() || !/^\d{6}$/.test(data.pincode.trim())) return "Enter a valid 6-digit pincode.";
    if (!data.societyEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.societyEmail.trim())) return "Enter a valid society email address.";
    return "";
  }

  async function next() {
    const err = validate();
    if (err) { setError(err); return; }
    setError("");
    await onNext();
  }

  return (
    <div style={S.card}>
      <div style={S.cardTitle}>Society Identity</div>
      <div style={S.cardSub}>Basic info about the society. You can change this later in Settings.</div>

      {error && <div style={S.error}>⚠ {error}</div>}

      <div style={S.field}>
        <label style={S.label}>Society Name</label>
        <input style={S.input} placeholder="e.g. Green Meadows Apartments"
          value={data.societyName} onChange={e => onChange("societyName", e.target.value)} />
      </div>

      <div style={S.field}>
        <label style={S.label}>Location</label>
        <input style={S.input} placeholder="e.g. Bandlaguda Jagir"
          value={data.location} onChange={e => onChange("location", e.target.value)} />
      </div>

      <div style={S.field}>
        <label style={S.label}>City</label>
        <input style={S.input} placeholder="e.g. Hyderabad"
          value={data.city} onChange={e => onChange("city", e.target.value)} />
      </div>

      <div style={{ display: "flex", gap: "10px" }}>
        <div style={{ ...S.field, flex: 1 }}>
          <label style={S.label}>Pincode</label>
          <input style={S.input} type="tel" inputMode="numeric" maxLength={6} placeholder="e.g. 500001"
            value={data.pincode} onChange={e => onChange("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))} />
        </div>
        <div style={{ ...S.field, flex: 1 }}>
          <label style={S.label}>Society Email</label>
          <input style={S.input} placeholder="e.g. society@example.com"
            value={data.societyEmail} onChange={e => onChange("societyEmail", e.target.value)} />
        </div>
      </div>

      <div style={S.btnRow}>
        <button style={{ ...S.btnSecondary, flex: 1 }} onClick={onBack} disabled={saving}>← Back</button>
        <button style={{ ...S.btnPrimary, flex: 2, marginTop: 0, opacity: saving ? 0.7 : 1, cursor: saving ? "not-allowed" : "pointer" }} onClick={next} disabled={saving}>{saving ? "Saving…" : "Next →"}</button>
      </div>
    </div>
  );
}

function Step1Billing({ data, onChange, onNext, onBack, saving }) {
  const [error, setError] = useState("");

  function validate() {
    if (!data.totalFlats || Number(data.totalFlats) < 1) return "Enter number of flats (min 1).";
    if (!data.occupiedRate || Number(data.occupiedRate) < 1) return "Enter occupied maintenance rate.";
    if (data.vacantMethod === "fixed" && (!data.vacantRate || Number(data.vacantRate) < 0))
      return "Enter vacant flat rate.";
    if (data.vacantMethod === "percentage" && (!data.vacantPct || Number(data.vacantPct) <= 0))
      return "Enter vacant percentage (e.g. 50).";
    return "";
  }

  async function next() {
    const err = validate();
    if (err) { setError(err); return; }
    setError("");
    await onNext();
  }

  return (
    <div style={S.card}>
      <div style={S.cardTitle}>Billing Settings</div>
      <div style={S.cardSub}>Flat count and maintenance rates. You can change this later in Settings.</div>

      {error && <div style={S.error}>⚠ {error}</div>}

      <div style={S.field}>
        <label style={S.label}>Total Number of Flats</label>
        <input style={S.input} type="number" min="1" placeholder="e.g. 12"
          value={data.totalFlats} onChange={e => onChange("totalFlats", e.target.value)} />
      </div>

      <div style={S.field}>
        <label style={S.label}>Occupied Flat Maintenance Rate (₹)</label>
        <input style={S.input} type="number" min="0" placeholder="e.g. 1500"
          value={data.occupiedRate} onChange={e => onChange("occupiedRate", e.target.value)} />
      </div>

      <div style={S.field}>
        <label style={S.label}>Vacant Flat Rate — Method</label>
        <select style={S.select} value={data.vacantMethod}
          onChange={e => onChange("vacantMethod", e.target.value)}>
          <option value="fixed">Fixed Amount</option>
          <option value="percentage">Percentage of Occupied Rate</option>
          <option value="perflat">Set Per Flat (in next step)</option>
        </select>
      </div>

      {data.vacantMethod === "fixed" && (
        <div style={S.field}>
          <label style={S.label}>Vacant Flat Rate (₹)</label>
          <input style={S.input} type="number" min="0" placeholder="e.g. 750"
            value={data.vacantRate} onChange={e => onChange("vacantRate", e.target.value)} />
        </div>
      )}

      {data.vacantMethod === "percentage" && (
        <div style={S.field}>
          <label style={S.label}>Vacant Rate % of Occupied (e.g. 50 = half)</label>
          <input style={S.input} type="number" min="1" max="100" placeholder="e.g. 50"
            value={data.vacantPct} onChange={e => onChange("vacantPct", e.target.value)} />
          {data.occupiedRate && data.vacantPct && (
            <div style={{ fontSize: "11px", color: "#6c63ff", marginTop: "4px" }}>
              → Vacant rate = ₹{Math.round(Number(data.occupiedRate) * Number(data.vacantPct) / 100)}
            </div>
          )}
        </div>
      )}

      <div style={S.field}>
        <label style={S.label}>Due Cutoff Day (1–28)</label>
        <input style={S.input} type="number" min="1" max="28" placeholder="e.g. 10"
          value={data.dueCutoffDay} onChange={e => onChange("dueCutoffDay", e.target.value)} />
      </div>

      <div style={S.field}>
        <label style={S.label}>Water Deposit / Fund (₹)</label>
        <input style={S.input} type="number" min="0" placeholder="e.g. 36000"
          value={data.waterDeposit} onChange={e => onChange("waterDeposit", e.target.value)} />
      </div>

      <div style={S.field}>
        <label style={S.label}>Corpus Fund (₹)</label>
        <input style={S.input} type="number" min="0" placeholder="e.g. 100000"
          value={data.corpusFund} onChange={e => onChange("corpusFund", e.target.value)} />
      </div>

      <div style={S.btnRow}>
        <button style={{ ...S.btnSecondary, flex: 1 }} onClick={onBack} disabled={saving}>← Back</button>
        <button style={{ ...S.btnPrimary, flex: 2, marginTop: 0, opacity: saving ? 0.7 : 1, cursor: saving ? "not-allowed" : "pointer" }} onClick={next} disabled={saving}>{saving ? "Saving…" : "Next →"}</button>
      </div>
    </div>
  );
}

// ── New Step 2: Auto-Reconciliation Choice ──────────────────────────
// A yes/no question, with plain-English explanation of what each
// option means. "Yes" leads into the Bank Alerts Email step; "No"
// skips straight to Flats — no email/app-password setup is asked of
// a society that wants manual entry.
function ReconciliationStep({ value, onChange, onNext, onBack, saving }) {
  const [error, setError] = useState("");

  function next() {
    if (!value) { setError("Please choose one option to continue."); return; }
    setError("");
    onNext(value);
  }

  return (
    <div style={S.card}>
      <div style={S.cardTitle}>Automatic Payment Matching</div>
      <div style={S.cardSub}>Should we automatically match bank payment alerts to residents, or will someone from your committee enter payments by hand?</div>

      {error && <div style={S.error}>⚠ {error}</div>}

      <button type="button" onClick={() => onChange("auto")}
        style={{
          width: "100%", textAlign: "left", padding: "16px", borderRadius: "12px", marginBottom: "12px", cursor: "pointer",
          border: value === "auto" ? "1px solid #6c63ff" : "1px solid #2e2e40",
          background: value === "auto" ? "#6c63ff22" : "#1b1d27",
        }}>
        <div style={{ fontWeight: "700", color: value === "auto" ? "#a29bff" : "#e8e8f0", marginBottom: "4px" }}>✅ Yes, match automatically</div>
        <div style={{ fontSize: "12px", color: "#8888a8" }}>We'll read your bank's payment alert emails and match each payment to the right flat automatically. Needs your bank-alert email and an app password (next step).</div>
      </button>

      <button type="button" onClick={() => onChange("manual")}
        style={{
          width: "100%", textAlign: "left", padding: "16px", borderRadius: "12px", marginBottom: "12px", cursor: "pointer",
          border: value === "manual" ? "1px solid #6c63ff" : "1px solid #2e2e40",
          background: value === "manual" ? "#6c63ff22" : "#1b1d27",
        }}>
        <div style={{ fontWeight: "700", color: value === "manual" ? "#a29bff" : "#e8e8f0", marginBottom: "4px" }}>✋ No, we'll enter payments manually</div>
        <div style={{ fontSize: "12px", color: "#8888a8" }}>Someone from your committee will mark payments as received inside the app themselves. No email or app password needed — this step will be skipped entirely.</div>
      </button>

      <div style={S.btnRow}>
        <button style={{ ...S.btnSecondary, flex: 1 }} onClick={onBack} disabled={saving}>← Back</button>
        <button style={{ ...S.btnPrimary, flex: 2, marginTop: 0, opacity: saving ? 0.7 : 1, cursor: saving ? "not-allowed" : "pointer" }} onClick={next} disabled={saving}>{saving ? "Saving…" : "Next →"}</button>
      </div>
    </div>
  );
}

// ── New Step 3: Bank Alerts Email ─────────────────────────────────
// Collects the society's bank-alert mailbox connection, used later by
// bank-sync.js to read payment alerts automatically. "Other" covers
// any provider we don't have a ready-made IMAP address for (including
// Yahoo, which needs a paid Yahoo Mail Plus plan for IMAP — so we
// don't offer it as a free-looking button, just let it through here).
const BANK_EMAIL_PROVIDERS = [
  { value: "gmail", label: "Gmail", host: "imap.gmail.com",
    helpUrl: "https://myaccount.google.com/apppasswords",
    helpText: "Create an app password at myaccount.google.com/apppasswords (needs 2-Step Verification turned on first)." },
  { value: "outlook", label: "Outlook", host: "outlook.office365.com",
    helpUrl: "https://account.microsoft.com/security",
    helpText: "Create an app password under Security settings at account.microsoft.com/security." },
  { value: "other", label: "Other", host: null,
    helpUrl: null,
    helpText: "Check with your email provider for how to create an app-specific password and their IMAP server address." },
];

function BankEmailStep({ data, onChange, onNext, onBack, saving }) {
  const [error, setError] = useState("");
  const [showPass, setShowPass] = useState(false);

  const selectedProvider = BANK_EMAIL_PROVIDERS.find(p => p.value === data.provider) || BANK_EMAIL_PROVIDERS[0];
  const hostLooksLikeYahoo = data.provider === "other" && /yahoo/i.test(data.host || "");

  function validate() {
    if (!data.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim()))
      return "Enter a valid email address.";
    if (data.provider === "other" && !data.host.trim())
      return "Enter the IMAP server address for this email provider.";
    if (!data.appPassword.trim())
      return "Enter the app password.";
    if (!data.mailboxFolder.trim())
      return "Enter a folder/label name for sorting bank alert emails.";
    return "";
  }

  function next() {
    const err = validate();
    if (err) { setError(err); return; }
    setError("");
    onNext();
  }

  return (
    <div style={S.card}>
      <div style={S.cardTitle}>Bank Alerts Email</div>
      <div style={S.cardSub}>So we can read your bank's payment alerts automatically.</div>

      {error && <div style={S.error}>⚠ {error}</div>}

      <div style={S.field}>
        <label style={S.label}>Which email ID have you shared with your society's bank to receive alerts?</label>
        <input style={S.input} type="email" placeholder="e.g. society@example.com"
          value={data.email} onChange={e => onChange("email", e.target.value)} />
      </div>

      <div style={S.field}>
        <label style={S.label}>Which email provider is this?</label>
        <div style={{ display: "flex", gap: "8px" }}>
          {BANK_EMAIL_PROVIDERS.map(p => (
            <button key={p.value} type="button" onClick={() => onChange("provider", p.value)}
              style={{
                flex: 1, padding: "10px", borderRadius: "8px", fontSize: "12px", fontWeight: "600",
                border: data.provider === p.value ? "1px solid #6c63ff" : "1px solid #2e2e40",
                background: data.provider === p.value ? "#6c63ff22" : "transparent",
                color: data.provider === p.value ? "#a29bff" : "#8888a8", cursor: "pointer",
              }}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {data.provider === "other" && (
        <div style={S.field}>
          <label style={S.label}>IMAP Server Address</label>
          <input style={S.input} placeholder="e.g. imap.mail.yahoo.com"
            value={data.host} onChange={e => onChange("host", e.target.value)} />
          {hostLooksLikeYahoo && (
            <div style={{ fontSize: "11px", color: "#ffa726", marginTop: "5px" }}>
              ⚠ Yahoo Mail needs a paid Yahoo Mail Plus plan to use IMAP — free Yahoo accounts won't connect.
            </div>
          )}
        </div>
      )}

      <div style={S.field}>
        <label style={S.label}>App Password</label>
        <div style={{ position: "relative" }}>
          <input style={{ ...S.input, paddingRight: "44px" }}
            type={showPass ? "text" : "password"} placeholder="16-character app password"
            value={data.appPassword}
            onChange={e => {
              // Google shows app passwords as "abcd efgh ijkl mnop" for
              // readability — strip those spaces automatically, since
              // copy-pasting them in raw would silently break the login.
              let val = e.target.value.replace(/\s/g, "");
              if (data.provider === "gmail") val = val.slice(0, 16);
              onChange("appPassword", val);
            }} />
          <button type="button" onClick={() => setShowPass(v => !v)}
            style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontSize: "16px" }}>
            {showPass ? "🙈" : "👁️"}
          </button>
        </div>
        {data.provider === "gmail" && (
          <div style={{ fontSize: "11px", color: data.appPassword.length === 16 ? "#4caf50" : "#8888a8", marginTop: "4px" }}>
            {data.appPassword.length}/16 characters
          </div>
        )}
        {selectedProvider.helpText && (
          <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "5px" }}>
            {selectedProvider.helpUrl
              ? <>Not sure how? <a href={selectedProvider.helpUrl} target="_blank" rel="noreferrer" style={{ color: "#6c63ff" }}>{selectedProvider.helpText}</a></>
              : selectedProvider.helpText}
          </div>
        )}
      </div>

      <div style={S.field}>
        <label style={S.label}>Folder / Label for Bank Alerts</label>
        <input style={S.input} placeholder="e.g. GREENMEADOWS-BANK"
          value={data.mailboxFolder} onChange={e => onChange("mailboxFolder", e.target.value)} />
        <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "5px" }}>
          Create this folder (Outlook) or label (Gmail) in your inbox, and make sure your bank's alert emails are filtered into it — this is where we'll look for payment alerts.
        </div>
      </div>

      <div style={S.btnRow}>
        <button style={{ ...S.btnSecondary, flex: 1 }} onClick={onBack} disabled={saving}>← Back</button>
        <button style={{ ...S.btnPrimary, flex: 2, marginTop: 0, opacity: saving ? 0.7 : 1, cursor: saving ? "not-allowed" : "pointer" }} onClick={next} disabled={saving}>{saving ? "Saving…" : "Next →"}</button>
      </div>
    </div>
  );
}

// ── Step 2: Flat Configuration ──────────────────────────────────
// ── Step 2: Flats + MC Role Upload ───────────────────────────────
// Replaces the old owner-only Excel upload AND the separate MC
// Accounts/password screen. One Excel upload carries full flat/owner
// data plus an "MC Role" column that identifies which flat's legal
// owner is President/Secretary/Treasurer — no manual MC contact form,
// no manual password entry. MC accounts get the same forced-change
// default password as every other owner account (see DEFAULT_OWNER_PASSWORD
// in saveFlatsStep).
const FLATS_TEMPLATE_COLUMNS = [
  "Flat No", "Block", "Floor", "Legal Owner Name", "Representative Name",
  "Flat Type", "Sq Ft", "Door No", "Property Tax No",
  "Electricity Service No", "Electricity USC No", "Mobile No1", "Mobile No2",
  "Email ID", "Status", "MC Role", "Opening Arrears (Months)",
];

const MC_ROLES_BY_SIZE = {
  "1": ["President"],
  "2": ["President", "Treasurer"],
  "3": ["President", "Secretary", "Treasurer"],
};

function Step2({ society, flats, setFlats, mcMemberCount, onNext, onBack, saving, progressText }) {
  const [error, setError] = useState("");

  // Compute vacant rate based on method
  function getVacantRate(status) {
    if (status === "Occupied") return Number(society.occupiedRate) || 0;
    if (society.vacantMethod === "fixed") return Number(society.vacantRate) || 0;
    if (society.vacantMethod === "percentage")
      return Math.round((Number(society.occupiedRate) || 0) * (Number(society.vacantPct) || 0) / 100);
    return 0; // perflat — user sets manually
  }

  function updateFlat(i, key, value) {
    setFlats(prev => {
      const updated = [...prev];
      updated[i] = { ...updated[i], [key]: value };
      // Auto-update rate when status changes (unless perflat)
      if (key === "status" && society.vacantMethod !== "perflat") {
        updated[i].rate = getVacantRate(value);
      }
      return updated;
    });
  }

  // Validates flat/contact fields, then checks the MC Role column against
  // the committee size chosen back in McSizeScreen: each required role
  // must appear on exactly one row, and no extra roles beyond that size.
  function validate() {
    for (let i = 0; i < flats.length; i++) {
      const f = flats[i];
      if (!f.flatNo.trim()) return `Row ${i + 1}: Flat number is required.`;
      if (!f.floor.trim()) return `Row ${i + 1}: Floor is required.`;
      if (normalizeFloor(f.floor) === null) return `Row ${i + 1} (${f.flatNo}): Floor "${f.floor}" isn't recognized — use Ground, 1st Floor, 2nd Floor, or 3rd Floor.`;
      if (!isValidMobile(f.mobile1)) return `Row ${i + 1} (${f.flatNo}): valid Mobile No1 is required (10 digits, or +1 and 10 digits for a US number).`;
      if (f.mobile2 && !isValidMobile(f.mobile2)) return `Row ${i + 1} (${f.flatNo}): Mobile No2 must be 10 digits, or +1 and 10 digits for a US number, or left blank.`;
      if (f.emailId && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.emailId.trim())) return `Row ${i + 1} (${f.flatNo}): Email ID looks invalid.`;
      const arrearsText = String(f.openingArrearsMonths ?? "").trim();
      if (arrearsText !== "" && (Number.isNaN(Number(arrearsText)) || Number(arrearsText) < 0)) return `Row ${i + 1} (${f.flatNo}): Opening Arrears (Months) must be a number (0 or more), or left blank.`;
    }

    const seenFlatNos = {};
    for (let i = 0; i < flats.length; i++) {
      const flatNo = flats[i].flatNo.trim().toUpperCase();
      if (seenFlatNos[flatNo]) return `Flat No "${flatNo}" appears more than once — each flat can appear only once.`;
      seenFlatNos[flatNo] = true;
    }

    const requiredRoles = MC_ROLES_BY_SIZE[mcMemberCount] || MC_ROLES_BY_SIZE["1"];
    const roleRows = {};
    for (let i = 0; i < flats.length; i++) {
      const role = (flats[i].mcRole || "").trim();
      if (!role) continue;
      if (!["President", "Secretary", "Treasurer"].includes(role))
        return `Row ${i + 1} (${flats[i].flatNo}): MC Role must be President, Secretary, or Treasurer.`;
      if (!requiredRoles.includes(role))
        return `Row ${i + 1} (${flats[i].flatNo}): MC Role "${role}" isn't used for a ${mcMemberCount}-member committee.`;
      if (roleRows[role]) return `MC Role "${role}" is set on more than one row (${roleRows[role]} and ${flats[i].flatNo}). Only one row per role.`;
      roleRows[role] = flats[i].flatNo;
    }
    for (const role of requiredRoles) {
      if (!roleRows[role]) return `MC Role "${role}" isn't set on any row — required for a ${mcMemberCount}-member committee.`;
    }

    return "";
  }

  async function next() {
    const err = validate();
    if (err) { setError(err); return; }
    setError("");
    await onNext();
  }

  function downloadSampleTemplate() {
    const sample = [
      FLATS_TEMPLATE_COLUMNS,
      ["G1", "A", "Ground", "Suresh Rao", "", "2BHK", "1100", "G1", "TAX12345",
        "ESN001", "USC001", "9876543210", "", "suresh.rao@example.com", "Occupied", "President", "0"],
    ];
    const ws = XLSX.utils.aoa_to_sheet(sample);
    ws["!cols"] = FLATS_TEMPLATE_COLUMNS.map(() => ({ wch: 16 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Flats");
    XLSX.writeFile(wb, "Flats_Upload_Template.xlsx");
  }

  function handleExcelUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setError("");

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: "binary" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

        const parsedRows = [];
        for (const r of rows) {
          const norm = {};
          for (const key of Object.keys(r)) norm[key.trim().toLowerCase()] = r[key];
          const flatNo = String(norm["flat no"] ?? norm["flatno"] ?? "").trim().toUpperCase();
          if (!flatNo) continue;
          const status = /vacant/i.test(String(norm["status"] ?? "")) ? "Vacant" : "Occupied";
          parsedRows.push({
            flatNo,
            block: String(norm["block"] ?? "").trim(),
            floor: normalizeFloor(norm["floor"]) ?? String(norm["floor"] ?? "").trim(),
            legalOwnerName: String(norm["legal owner name"] ?? norm["legalownername"] ?? "").trim(),
            representativeName: String(norm["representative name"] ?? "").trim(),
            flatType: String(norm["flat type"] ?? "").trim(),
            sqFt: String(norm["sq ft"] ?? norm["sqft"] ?? "").trim(),
            doorNo: String(norm["door no"] ?? "").trim(),
            propertyTaxNo: String(norm["property tax no"] ?? "").trim(),
            electricityServiceNo: String(norm["electricity service no"] ?? "").trim(),
            electricityUscNo: String(norm["electricity usc no"] ?? "").trim(),
            mobile1: normalizeMobile(norm["mobile no1"] ?? norm["mobile no 1"] ?? ""),
            mobile2: normalizeMobile(norm["mobile no2"] ?? norm["mobile no 2"] ?? ""),
            emailId: String(norm["email id"] ?? "").trim(),
            status,
            mcRole: String(norm["mc role"] ?? "").trim(),
            openingArrearsMonths: String(norm["opening arrears (months)"] ?? norm["opening arrears"] ?? "").trim(),
            rate: status === "Occupied" ? getVacantRate("Occupied") : getVacantRate("Vacant"),
          });
        }

        if (parsedRows.length === 0) {
          setError("No rows found with a Flat No. Check the sheet has a \"Flat No\" column filled in.");
          return;
        }

        // Catch duplicate Flat No rows right at upload — a duplicate would
        // otherwise silently overwrite the earlier row when saved, with no
        // warning to the President uploading the sheet.
        const seen = {};
        const duplicates = new Set();
        for (const r of parsedRows) {
          if (seen[r.flatNo]) duplicates.add(r.flatNo);
          seen[r.flatNo] = true;
        }
        if (duplicates.size > 0) {
          setError(`Duplicate Flat No in the sheet: ${[...duplicates].join(", ")}. Each flat can appear only once — fix the sheet and upload again.`);
          return;
        }

        // Replaces the flats list outright — the sheet IS the data entry,
        // not a fill-in for rows typed by hand first (an upload can't
        // match against Flat Nos that don't exist yet).
        setFlats(parsedRows);
      } catch (err) {
        setError("Couldn't read that file. Make sure it's a valid .xlsx or .csv.");
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = ""; // allow re-uploading the same file name
  }

  return (
    <div style={S.card}>
      <div style={S.cardTitle}>Flats & Managing Committee</div>
      <div style={S.cardSub}>
        {flats.length} flats. Upload once to fill everything below, including who's President/Secretary/Treasurer
        via the MC Role column — or edit any row by hand.
      </div>

      {error && <div style={S.error}>⚠ {error}</div>}

      <div style={{ marginBottom: "14px" }}>
        <label style={{ ...S.btnSecondary, display: "inline-flex", alignItems: "center", justifyContent: "center", width: "auto", padding: "10px 16px", cursor: "pointer" }}>
          📤 Upload Flats (Excel/CSV)
          <input type="file" accept=".xlsx,.xls,.csv" onChange={handleExcelUpload} style={{ display: "none" }} />
        </label>
        <button
          type="button"
          onClick={downloadSampleTemplate}
          style={{ background: "none", border: "none", color: "#6c63ff", fontSize: "12px", marginLeft: "10px", cursor: "pointer", textDecoration: "underline" }}
        >
          Download sample template
        </button>
        <div style={{ fontSize: "11px", color: "#555570", marginTop: "6px" }}>
          Columns: {FLATS_TEMPLATE_COLUMNS.join(", ")} — matched to the rows below by Flat No.
          MC Role required on exactly {mcMemberCount} row(s): {(MC_ROLES_BY_SIZE[mcMemberCount] || MC_ROLES_BY_SIZE["1"]).join(", ")}.
        </div>
      </div>

      {/* Flats — one card per flat, fields stacked so nothing needs horizontal scrolling on mobile */}
      {flats.map((f, i) => (
        <div key={i} style={{
          border: "1px solid #2e2e40", borderRadius: "12px", padding: "16px",
          marginBottom: "16px", background: "#14141c",
        }}>
          <div style={{ fontSize: "12px", fontWeight: "700", color: "#a29bff", marginBottom: "12px" }}>
            Flat {i + 1}{f.mcRole ? ` · ${f.mcRole}` : ""}
          </div>

          <div style={S.field}>
            <label style={S.label}>Flat No</label>
            <input style={S.input} placeholder="G1"
              value={f.flatNo} onChange={e => updateFlat(i, "flatNo", e.target.value.toUpperCase())} />
          </div>

          <div style={S.field}>
            <label style={S.label}>Block</label>
            <input style={S.input} placeholder="A"
              value={f.block} onChange={e => updateFlat(i, "block", e.target.value)} />
          </div>

          <div style={S.field}>
            <label style={S.label}>Floor</label>
            <input style={S.input} placeholder="Ground"
              value={f.floor} onChange={e => updateFlat(i, "floor", e.target.value)} />
          </div>

          <div style={S.field}>
            <label style={S.label}>Legal Owner Name</label>
            <input style={S.input} placeholder="Owner name"
              value={f.legalOwnerName} onChange={e => updateFlat(i, "legalOwnerName", e.target.value)} />
          </div>

          <div style={S.field}>
            <label style={S.label}>Representative Name</label>
            <input style={S.input} placeholder="If different from owner"
              value={f.representativeName} onChange={e => updateFlat(i, "representativeName", e.target.value)} />
          </div>

          <div style={S.field}>
            <label style={S.label}>Flat Type</label>
            <input style={S.input} placeholder="2BHK"
              value={f.flatType} onChange={e => updateFlat(i, "flatType", e.target.value)} />
          </div>

          <div style={S.field}>
            <label style={S.label}>Sq Ft</label>
            <input style={S.input} type="number" min="0" placeholder="1100"
              value={f.sqFt} onChange={e => updateFlat(i, "sqFt", e.target.value)} />
          </div>

          <div style={S.field}>
            <label style={S.label}>Door No</label>
            <input style={S.input} placeholder="G1"
              value={f.doorNo} onChange={e => updateFlat(i, "doorNo", e.target.value)} />
          </div>

          <div style={S.field}>
            <label style={S.label}>Property Tax No</label>
            <input style={S.input} value={f.propertyTaxNo} onChange={e => updateFlat(i, "propertyTaxNo", e.target.value)} />
          </div>

          <div style={S.field}>
            <label style={S.label}>Electricity Service No</label>
            <input style={S.input} value={f.electricityServiceNo} onChange={e => updateFlat(i, "electricityServiceNo", e.target.value)} />
          </div>

          <div style={S.field}>
            <label style={S.label}>Electricity USC No</label>
            <input style={S.input} value={f.electricityUscNo} onChange={e => updateFlat(i, "electricityUscNo", e.target.value)} />
          </div>

          <div style={S.field}>
            <label style={S.label}>Mobile No1</label>
            <input style={S.input} type="tel" inputMode="tel" placeholder="10-digit (US: +1 and 10 digits)"
              value={f.mobile1} onChange={e => updateFlat(i, "mobile1", cleanMobileTyping(e.target.value))}
              onBlur={e => updateFlat(i, "mobile1", normalizeMobile(e.target.value))} />
          </div>

          <div style={S.field}>
            <label style={S.label}>Mobile No2 (optional)</label>
            <input style={S.input} type="tel" inputMode="tel" placeholder="10-digit (US: +1 and 10 digits)"
              value={f.mobile2} onChange={e => updateFlat(i, "mobile2", cleanMobileTyping(e.target.value))}
              onBlur={e => updateFlat(i, "mobile2", normalizeMobile(e.target.value))} />
          </div>

          <div style={S.field}>
            <label style={S.label}>Email ID (optional)</label>
            <input style={S.input} type="email" placeholder="owner@example.com"
              value={f.emailId} onChange={e => updateFlat(i, "emailId", e.target.value)} />
          </div>

          <div style={S.field}>
            <label style={S.label}>Status</label>
            <select style={{ ...S.input, cursor: "pointer" }} value={f.status}
              onChange={e => updateFlat(i, "status", e.target.value)}>
              <option value="Occupied">Occupied</option>
              <option value="Vacant">Vacant</option>
            </select>
          </div>

          <div style={S.field}>
            <label style={S.label}>Rate ₹</label>
            <input style={S.input} type="number"
              value={f.rate} onChange={e => updateFlat(i, "rate", e.target.value)} />
          </div>

          <div style={S.field}>
            <label style={S.label}>MC Role</label>
            <select style={{ ...S.input, cursor: "pointer" }} value={f.mcRole}
              onChange={e => updateFlat(i, "mcRole", e.target.value)}>
              <option value="">— None —</option>
              <option value="President">President</option>
              <option value="Secretary">Secretary</option>
              <option value="Treasurer">Treasurer</option>
            </select>
          </div>

          <div style={S.field}>
            <label style={S.label}>Opening Arrears (Months) (optional)</label>
            <input style={S.input} type="number" min="0"
              value={f.openingArrearsMonths ?? ""} onChange={e => updateFlat(i, "openingArrearsMonths", e.target.value)} />
          </div>
        </div>
      ))}

      <div style={{ fontSize: "11px", color: "#555570", marginTop: "10px", marginBottom: "4px" }}>
        Mobile No1 is compulsory for occupied flats — a login account is created for each, with password{" "}
        <code>Welcome@123</code>. They should change it on first login. Vacant flats can be left blank.
        Only a flat's legal owner can hold an MC Role.
      </div>

      <div style={S.btnRow}>
        <button style={{ ...S.btnSecondary, flex: 1 }} onClick={onBack} disabled={saving}>← Back</button>
        <button style={{ ...S.btnPrimary, flex: 2, marginTop: 0, opacity: saving ? 0.7 : 1, cursor: saving ? "not-allowed" : "pointer" }} onClick={next} disabled={saving}>{saving ? (progressText || "Saving…") : "Next →"}</button>
      </div>
    </div>
  );
}


// ── New Step 5: Pricing & Terms ─────────────────────────────────
// Reads the flat count already entered in Step 1 — never asks again.
// Pricing tiers themselves live in Firestore (config/pricing), edited
// through abc-dashboard's own Pricing Settings screen, not hardcoded
// here — so Dr. Vijay can change prices without a code change. See
// Adyantam_Pricing_Decision_Notes, Section 7.3, for the reasoning.
// Triggers the Terms & Conditions PDF to download automatically — used
// right when Confirm succeeds, so every new society actually receives
// a copy, rather than relying on them remembering to click a link.
function autoDownloadTerms() {
  const a = document.createElement("a");
  a.href = termsAndConditionsPdf;
  a.download = "Adyantam-Terms-and-Conditions.pdf";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function findMatchingTier(totalFlats, pricing) {
  if (!pricing || !Array.isArray(pricing.tiers)) return null;
  const flats = Number(totalFlats);
  const tier = pricing.tiers.find(t => flats >= t.minFlats && flats <= t.maxFlats);
  if (tier) return { type: "tier", ...tier };
  if (pricing.customAboveFlats && flats > pricing.customAboveFlats) {
    return { type: "custom", message: pricing.customMessage || "Please contact us to discuss pricing for your society." };
  }
  return null;
}

function PricingStep({ society, onNext, onBack, saving, agreed, setAgreed, pricing, pricingLoading, pricingError }) {
  const [error, setError] = useState("");
  const match = findMatchingTier(society.totalFlats, pricing);

  function next() {
    if (!match) { setError("Pricing isn't available right now — please try again in a moment."); return; }
    if (match.type === "tier" && !agreed) { setError("Please confirm you agree to this pricing to continue."); return; }
    setError("");
    onNext(match);
  }

  return (
    <div style={S.card}>
      <div style={S.cardTitle}>Pricing & Terms</div>
      <div style={S.cardSub}>Based on {society.totalFlats} flats, entered earlier.</div>

      {error && <div style={S.error}>⚠ {error}</div>}

      {pricingLoading && <div style={{ color: "#8888a8", fontSize: "14px" }}>Loading current pricing…</div>}

      {pricingError && !pricingLoading && (
        <div style={S.error}>⚠ Couldn't load pricing: {pricingError}</div>
      )}

      {!pricingLoading && !pricingError && match && match.type === "tier" && (
        <>
          <div style={{ background: "#1b1d27", border: "1px solid #2e2e40", borderRadius: "12px", padding: "20px", marginBottom: "16px", textAlign: "center" }}>
            <div style={{ fontSize: "13px", color: "#8888a8", marginBottom: "6px" }}>{match.label}</div>
            <div style={{ fontSize: "32px", fontWeight: "800", color: "#a29bff" }}>₹{match.priceMonthly}<span style={{ fontSize: "14px", color: "#8888a8", fontWeight: "400" }}> / month</span></div>
          </div>

          <div style={{ fontSize: "13px", color: "#8888a8", marginBottom: "16px" }}>
            Your first 45 days are free — this price applies after the trial ends. You can cancel anytime before then with no charge.
          </div>

          <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", fontSize: "13px", color: "#e8e8f0", cursor: "pointer", marginBottom: "16px" }}>
            <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
              style={{ marginTop: "3px" }} />
            <span>I agree to this pricing (₹{match.priceMonthly}/month after trial period of 45 days).</span>
          </label>
        </>
      )}

      {!pricingLoading && !pricingError && match && match.type === "custom" && (
        <div style={{ background: "#1b1d27", border: "1px solid #2e2e40", borderRadius: "12px", padding: "20px", marginBottom: "16px" }}>
          {match.message}
        </div>
      )}

      {!pricingLoading && !pricingError && !match && (
        <div style={S.error}>⚠ No pricing is set up for {society.totalFlats} flats yet — please contact support.</div>
      )}

      <div style={S.btnRow}>
        <button style={{ ...S.btnSecondary, flex: 1 }} onClick={onBack} disabled={saving}>← Back</button>
        <button style={{ ...S.btnPrimary, flex: 2, marginTop: 0, opacity: saving ? 0.7 : 1, cursor: saving ? "not-allowed" : "pointer" }} onClick={next} disabled={saving || pricingLoading}>{saving ? "Saving…" : "Next →"}</button>
      </div>
    </div>
  );
}

// ── Step 4: Review & Confirm ────────────────────────────────────
function Step4({ society, flats, bankSync, agreedPricing, reconciliationMethod, onBack, onConfirm, saving, progressText }) {
  const vacantRateDisplay = () => {
    if (society.vacantMethod === "fixed") return `₹${society.vacantRate}`;
    if (society.vacantMethod === "percentage") return `${society.vacantPct}% = ₹${Math.round(Number(society.occupiedRate) * Number(society.vacantPct) / 100)}`;
    return "Set per flat";
  };

  return (
    <div style={S.card}>
      <div style={S.cardTitle}>Review & Confirm</div>
      <div style={S.cardSub}>Check everything before saving. You can go back to edit.</div>

      {/* Society */}
      <div style={S.reviewSection}>
        <div style={S.reviewTitle}>Society</div>
        {[
          ["Name", society.societyName],
          ["Location", society.location],
          ["City", society.city],
          ["Total Flats", society.totalFlats],
          ["Occupied Rate", `₹${society.occupiedRate}`],
          ["Vacant Rate", vacantRateDisplay()],
          ["Due Cutoff Day", society.dueCutoffDay],
          ["Water Deposit", society.waterDeposit ? `₹${society.waterDeposit}` : "—"],
          ["Corpus Fund", society.corpusFund ? `₹${society.corpusFund}` : "—"],
          ["Water Bill Approval", society.waterApprovalFlow === "auto" ? "Auto (no approval)" : society.waterApprovalFlow === "single" ? "Single Step (President)" : "Two Step (Sec/Treas → President)"],
        ].map(([k, v]) => (
          <div key={k} style={S.reviewRow}>
            <span style={S.reviewKey}>{k}</span>
            <span style={S.reviewVal}>{v}</span>
          </div>
        ))}
      </div>

      {/* Bank Alerts */}
      <div style={S.reviewSection}>
        <div style={S.reviewTitle}>Payment Matching</div>
        {reconciliationMethod === "manual" ? (
          <div style={S.reviewRow}>
            <span style={S.reviewVal}>Manual entry — committee will mark payments received in the app</span>
          </div>
        ) : (
          [
            ["Email", bankSync.email],
            ["Provider", BANK_EMAIL_PROVIDERS.find(p => p.value === bankSync.provider)?.label || bankSync.provider],
            ["App Password", (bankSync.appPassword || bankSync.passwordSaved) ? "•".repeat(8) + " (saved)" : "—"],
            ["Folder / Label", bankSync.mailboxFolder],
          ].map(([k, v]) => (
            <div key={k} style={S.reviewRow}>
              <span style={S.reviewKey}>{k}</span>
              <span style={S.reviewVal}>{v}</span>
            </div>
          ))
        )}
      </div>

      {/* Pricing */}
      <div style={S.reviewSection}>
        <div style={S.reviewTitle}>Pricing</div>
        {agreedPricing ? (
          <div style={S.reviewRow}>
            <span style={S.reviewKey}>{agreedPricing.label}</span>
            <span style={S.reviewVal}>₹{agreedPricing.priceMonthly}/month after the {TRIAL_DAYS}-day trial</span>
          </div>
        ) : (
          <div style={S.reviewRow}>
            <span style={S.reviewVal}>Custom pricing — to be discussed directly</span>
          </div>
        )}
      </div>

      {/* Trial Period */}
      <div style={S.reviewSection}>
        <div style={S.reviewTitle}>Trial Period</div>
        {(() => {
          const trialStart = new Date();
          const trialEnd = new Date(trialStart.getTime() + TRIAL_DAYS * 24 * 60 * 60 * 1000);
          const fmt = (d) => fmtD(d);
          return (
            <>
              <div style={S.reviewRow}>
                <span style={S.reviewKey}>Starts</span>
                <span style={S.reviewVal}>{fmt(trialStart)} (on Confirm)</span>
              </div>
              <div style={S.reviewRow}>
                <span style={S.reviewKey}>Ends</span>
                <span style={S.reviewVal}>{fmt(trialEnd)}</span>
              </div>
            </>
          );
        })()}
      </div>

      {/* Flats summary */}
      <div style={S.reviewSection}>
        <div style={S.reviewTitle}>Flats ({flats.length})</div>
        {flats.slice(0, 5).map((f, i) => (
          <div key={i} style={S.reviewRow}>
            <span style={S.reviewKey}>{f.flatNo} · {f.floor}</span>
            <span style={S.reviewVal}>{f.legalOwnerName || "—"} · {f.status} · ₹{f.rate}{f.mcRole ? ` · ${f.mcRole}` : ""}</span>
          </div>
        ))}
        {flats.length > 5 && (
          <div style={{ fontSize: "11px", color: "#555570" }}>…and {flats.length - 5} more</div>
        )}
      </div>

      <div style={{ fontSize: "11px", color: "#555570", marginBottom: "12px" }}>
        Bank details, UPI IDs, and additional MC members can be added from Settings after login.
      </div>

      <div style={S.btnRow}>
        <button style={{ ...S.btnSecondary, flex: 1 }} onClick={onBack} disabled={saving}>← Back</button>
        <button
          style={{ ...S.btnPrimary, flex: 2, marginTop: 0, opacity: saving ? 0.7 : 1, cursor: saving ? "not-allowed" : "pointer" }}
          onClick={onConfirm} disabled={saving}>
          {saving ? (progressText || "Setting up…") : "✅ Confirm & Setup"}
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN SETUP WIZARD
// ══════════════════════════════════════════════════════════════
// ── Last screen (added 7 Oct 2026): shows the result of the set-up, including the first
// month's bills, BEFORE the wizard closes. The Done button is what closes it. ──
function monthLabel(key) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(key || ""));
  if (!m) return "this month";
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[Number(m[2]) - 1]}-${m[1]}`;
}

function DoneStep({ societyId, bills, messages, handoffCount, onDone }) {
  // Added 8 Oct 2026: a hand-over password exists only on this screen, so Done waits for a tick.
  const [noted, setNoted] = useState(false);
  const mustTick = (handoffCount || 0) > 0;
  const label = monthLabel(bills && bills.month);
  const problems = (bills && bills.problems) || [];
  let billLine;
  let billOk = false;
  if (!bills) {
    billLine = "Bills for this month: the server did not report them. Please check the bills of this society.";
  } else if (bills.error) {
    billLine = `Bills for ${label} were NOT created: ${bills.error}. Tell the Owner. Until bills exist, bank payments of this society cannot be matched automatically.`;
  } else {
    billOk = true;
    billLine = `Bills for ${label} created for ${bills.created} flat(s)` + (bills.skipped ? ` (${bills.skipped} already existed)` : "") + ".";
  }
  const okBox = {
    background: "#00c85315", border: "1px solid #00c85340", borderRadius: "10px",
    padding: "10px 14px", fontSize: "13px", color: "#00c853", marginBottom: "12px",
  };
  return (
    <div style={S.card}>
      <div style={S.cardTitle}>Society is set up</div>
      <div style={S.cardSub}>Setup ID: {societyId}</div>
      <div style={{ ...(billOk ? okBox : S.error), whiteSpace: "pre-line" }}>
        {billOk ? "✓ " : "⚠ "}{billLine}
      </div>
      {problems.length > 0 && (
        <div style={{ ...S.error, whiteSpace: "pre-line" }}>
          {"⚠ " + problems.length + " flat(s) got no bill:\n" + problems.join("\n")}
        </div>
      )}
      {(messages || []).map((m, i) => (
        <div key={i} style={{ ...S.error, whiteSpace: "pre-line" }}>⚠ {m}</div>
      ))}
      {mustTick && (
        <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#e8e8f0", margin: "4px 0 12px", cursor: "pointer" }}>
          <input type="checkbox" checked={noted} onChange={(e) => setNoted(e.target.checked)} />
          I have noted the passwords
        </label>
      )}
      <button style={mustTick && !noted ? { ...S.btnPrimary, opacity: 0.4, cursor: "not-allowed" } : S.btnPrimary} disabled={mustTick && !noted} onClick={onDone}>Done</button>
    </div>
  );
}

export default function SetupWizard({ user, onComplete }) {
  const [flowStage, setFlowStage] = useState("welcome"); // "welcome" | "resume" | "customerIntro" | "features" | "wizard"
  const [welcomeScreen, setWelcomeScreen] = useState(0); // preserved so Back from Stage 2 lands on the last tour screen, not screen 1
  const [introScreen, setIntroScreen] = useState("info"); // "info" | "mcSize" — Stage 2's own two screens
  const [featuresScreen, setFeaturesScreen] = useState("mandatory"); // "mandatory" | "optional" — Stage 3's own two screens

  // Stage 3 data — mandatory features always true; optional ones default on.
  const [featuresEnabled, setFeaturesEnabled] = useState(() =>
    Object.fromEntries(FEATURES.map((f) => [f.key, true]))
  );
  const [waterBillingMode, setWaterBillingMode] = useState("meter");

  function toggleFeature(key, value) {
    setFeaturesEnabled((prev) => ({ ...prev, [key]: value }));
  }
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  // Stage 2 data — mcMemberCount decides how many MC Role slots the
  // Flats Excel upload (Step 2 of the wizard) must fill later. Saved
  // immediately to its own "leads" record on Next, so nothing is lost
  // if the visitor closes the app before reaching Stage 5.
  const [customer, setCustomer] = useState({ mcMemberCount: "1" });
  const [leadId, setLeadId] = useState(null);
  const [customerSaving, setCustomerSaving] = useState(false);

  function updateMcMemberCount(count) {
    setCustomer(prev => ({ ...prev, mcMemberCount: count }));
  }

  async function saveCustomerIntro() {
    setCustomerSaving(true);
    try {
      const leadRef = leadId ? doc(db, "leads", leadId) : doc(collection(db, "leads"));
      await setDoc(leadRef, {
        mcMemberCount: customer.mcMemberCount,
        updatedAt: new Date().toISOString(),
      }, { merge: true });
      if (!leadId) setLeadId(leadRef.id);
      setFlowStage("features");
    } catch (e) {
      console.error("Saving customer intro failed:", e);
      // Per design, a visitor's details should never be held back or lost —
      // move on even if the background save failed; nothing entered here is discarded.
      setFlowStage("features");
    } finally {
      setCustomerSaving(false);
    }
  }

  // ── Resume Setup → hydrates every piece of state from a previously
  // saved draft, then drops the person on the furthest step they'd
  // actually completed (see fetchDraftState in ResumeSetup.jsx). ──
  function applyResumedDraft(draft) {
    setSocietyId(draft.societyId);
    setSociety(prev => ({ ...prev, ...draft.society }));
    setCustomer(prev => ({ ...prev, mcMemberCount: draft.mcMemberCount }));
    if (draft.featuresEnabled) setFeaturesEnabled(draft.featuresEnabled);
    setWaterBillingMode(draft.waterBillingMode);
    if (draft.reconciliationMethod) setReconciliationMethod(draft.reconciliationMethod);
    if (draft.bankSync) setBankSync(prev => ({ ...prev, ...draft.bankSync }));
    if (draft.flats.length > 0) setFlats(draft.flats);
    if (draft.pricing) {
      setPricingAgreed(true);
      setAgreedPricing(draft.pricing.type === "tier" ? draft.pricing : null);
    }
    setStep(draft.resumeStep);
    setFlowStage("wizard");
  }


  // Set once Step 1 is first saved — every later step writes under this ID
  // instead of waiting for final Confirm, so nothing is lost if the person
  // closes the app partway through setup.
  const [societyId, setSocietyId] = useState(null);
  const [step2Progress, setStep2Progress] = useState("");
  const [confirmProgress, setConfirmProgress] = useState("");
  const [doneInfo, setDoneInfo] = useState(null); // result shown on the last screen

  // Step 1 data
  const [society, setSociety] = useState({
    societyName: "", location: "", city: "", pincode: "", societyEmail: "", totalFlats: "",
    occupiedRate: "", vacantMethod: "fixed", vacantRate: "", vacantPct: "",
    dueCutoffDay: "10", waterDeposit: "", corpusFund: "", waterApprovalFlow: "two-step",
  });

  // New Step 2 data — whether the society wants automatic bank-alert
  // payment matching, or manual entry. Determines whether the Bank
  // Alerts Email step is shown at all.
  const [reconciliationMethod, setReconciliationMethod] = useState(null); // "auto" | "manual"

  // New Step 3 data — the society's bank-alert mailbox connection, used
  // later by bank-sync.js to read payment alerts automatically. Shape
  // here must match exactly what bank-sync.js expects under
  // societies/{id}/settings/init → bankSync (see PER-SOCIETY MAILBOX
  // SETTINGS block in that file): { method, provider, host, email,
  // appPassword, mailboxFolder }.
  const [bankSync, setBankSync] = useState({
    provider: "gmail", // "gmail" | "outlook" | "other"
    host: "", // only used/asked when provider === "other"
    email: "",
    appPassword: "",
    mailboxFolder: "",
    passwordSaved: false, // true once the password is locked in the vault
  });

  // New Step 5 data — pricing tiers are fetched from Firestore
  // (config/pricing), NOT hardcoded — edited via abc-dashboard's own
  // Pricing Settings screen. agreedPricing is set once the customer
  // ticks "I agree" and moves on; saved into settings/init at Confirm.
  const [pricing, setPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [pricingError, setPricingError] = useState("");
  const [pricingAgreed, setPricingAgreed] = useState(false);
  const [agreedPricing, setAgreedPricing] = useState(null);

  // Step 2 data — flats array, rebuilt when totalFlats changes. MC Role
  // (President/Secretary/Treasurer) lives per-row here too — no separate
  // MC accounts array; the flat's legal owner IS the MC account when a
  // role is set on that row.
  const [flats, setFlats] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, "config", "pricing"));
        if (snap.exists()) {
          setPricing(snap.data());
        } else {
          setPricingError("Pricing hasn't been set up yet.");
        }
      } catch (err) {
        console.error("Pricing fetch failed:", err);
        setPricingError(err.message || "Unknown error.");
      } finally {
        setPricingLoading(false);
      }
    })();
  }, []);

  function updateSociety(key, value) {
    setSociety(prev => {
      const updated = { ...prev, [key]: value };
      // Rebuild flats when totalFlats changes
      if (key === "totalFlats") {
        const n = Math.max(0, Math.min(200, parseInt(value) || 0));
        const occupied = Number(updated.occupiedRate) || 0;
        setFlats(Array.from({ length: n }, (_, i) => ({
          flatNo: "", block: "", floor: "", legalOwnerName: "", representativeName: "",
          flatType: "", sqFt: "", doorNo: "", propertyTaxNo: "",
          electricityServiceNo: "", electricityUscNo: "", mobile1: "", mobile2: "",
          emailId: "", status: "Occupied", mcRole: "", openingArrearsMonths: "", rate: occupied,
        })));
      }
      // Update all flat rates when rate settings change
      if (["occupiedRate", "vacantRate", "vacantPct", "vacantMethod"].includes(key)) {
        setFlats(prev => prev.map(f => {
          if (updated.vacantMethod === "perflat") return f;
          const occupied = Number(updated.occupiedRate) || 0;
          if (f.status === "Occupied") return { ...f, rate: occupied };
          if (updated.vacantMethod === "fixed") return { ...f, rate: Number(updated.vacantRate) || 0 };
          if (updated.vacantMethod === "percentage")
            return { ...f, rate: Math.round(occupied * (Number(updated.vacantPct) || 0) / 100) };
          return f;
        }));
      }
      return updated;
    });
  }

  function updateBankSync(key, value) {
    setBankSync(prev => ({ ...prev, [key]: value }));
  }

  // ── Step 0 → creates the society (status "draft") on first save,
  // writes identity fields to settings/init on every save (including
  // re-visits) ──
  async function saveStep1Identity() {
    setSaving(true);
    setSaveError("");
    try {
      let sid = societyId;

      if (!sid) {
        sid = await generateUniqueSocietyId();
        const creatorUid = await ensureLocalAnonAuth();
        await setDoc(doc(db, "societies", sid), {
          societyId: sid,
          name: society.societyName.trim(),
          status: "draft", // flips to "trial" only at final Confirm
          createdAt: new Date().toISOString(),
          trialEndsAt: null,
          leadId: leadId || null, // links back to the Stage 2 "leads" record, if any
          // Lets Firestore rules verify who's allowed to keep editing this
          // draft society before any MC member doc exists to check against.
          createdByAuthUid: creatorUid,
          // The logged-in platform user (Sales rep or Owner) who ran this
          // setup — distinct from createdByAuthUid above, which is just an
          // anonymous Firebase Auth session for Firestore rules purposes.
          createdByEmail: user?.email || null,
          createdByName: user?.name || user?.email || null,
        });
        setSocietyId(sid);
      } else {
        // Re-visited Identity screen — keep the same ID/code, just update the name
        await setDoc(doc(db, "societies", sid), { name: society.societyName.trim() }, { merge: true });
      }

      await setDoc(doc(db, "societies", sid, "settings", "init"), {
        societyId: sid,
        societyName: society.societyName.trim(),
        location: society.location.trim(),
        city: society.city.trim(),
        pincode: society.pincode.trim(),
        societyEmail: society.societyEmail.trim(),
        mcMemberCount: customer.mcMemberCount,
        createdAt: new Date().toISOString(),
      }, { merge: true });

      setStep(1);
    } catch (err) {
      console.error("Society Identity save failed:", err);
      setSaveError("Couldn't save: " + (err.message || "Unknown error. Check your connection."));
    } finally {
      setSaving(false);
    }
  }

  // ── Step 1 → writes billing fields to settings/init ──
  async function saveStep1Billing() {
    setSaving(true);
    setSaveError("");
    try {
      const occupiedRate = Number(society.occupiedRate);
      let vacantRate = 0;
      if (society.vacantMethod === "fixed") vacantRate = Number(society.vacantRate);
      else if (society.vacantMethod === "percentage")
        vacantRate = Math.round(occupiedRate * Number(society.vacantPct) / 100);

      await setDoc(doc(db, "societies", societyId, "settings", "init"), {
        totalFlats: Number(society.totalFlats),
        maintenanceAmt: occupiedRate,
        vacantAmt: vacantRate,
        vacantMethod: society.vacantMethod,
        vacantPct: society.vacantMethod === "percentage" ? Number(society.vacantPct) : null,
        dueCutoffDay: Number(society.dueCutoffDay) || 10,
        waterDeposit: Number(society.waterDeposit) || 0,
        corpusFund: Number(society.corpusFund) || 0,
        tankerRate: 0,
        waterApprovalFlow: society.waterApprovalFlow || "two-step",
        features: featuresEnabled, // Stage 3 toggles — gates which modules this society sees
        waterBillingMethod: featuresEnabled.water ? waterBillingMode : "not_needed", // "meter" | "equal" | "not_needed" — must match WaterScreen.jsx's router exactly
        bankAccount: "", ifsc: "", upi1: "", upi2: "",
      }, { merge: true });

      setStep(2);
    } catch (err) {
      console.error("Billing Settings save failed:", err);
      setSaveError("Couldn't save: " + (err.message || "Unknown error. Check your connection."));
    } finally {
      setSaving(false);
    }
  }

  // ── Step 2 → saves the reconciliation choice, then either goes
  // to Bank Alerts Email (auto) or skips straight to Flats (manual) ──
  async function saveReconciliation(choice) {
    setSaving(true);
    setSaveError("");
    try {
      await setDoc(doc(db, "societies", societyId, "settings", "init"), {
        reconciliationMethod: choice,
      }, { merge: true });

      setReconciliationMethod(choice);
      if (choice === "auto" && society.societyEmail && !bankSync.email) {
        setBankSync(prev => ({ ...prev, email: society.societyEmail }));
      }
      setStep(choice === "auto" ? 3 : 4);
    } catch (err) {
      console.error("Reconciliation save failed:", err);
      setSaveError("Couldn't save: " + (err.message || "Unknown error. Check your connection."));
    } finally {
      setSaving(false);
    }
  }

  // ── Step 3 → saves the society's bank-alert mailbox connection.
  // Written as its own "bankSync" object under settings/init, in the
  // exact shape bank-sync.js expects — see the state comment above. ──
  async function saveBankEmail() {
    setSaving(true);
    setSaveError("");
    try {
      // 1) The password goes only to the locked vault in the main app.
      await saveMailboxLoginToVault({
        societyId,
        provider: bankSync.provider,
        host: bankSync.provider === "other" ? bankSync.host.trim() : "",
        email: bankSync.email.trim(),
        mailboxFolder: bankSync.mailboxFolder.trim(),
        appPassword: bankSync.appPassword,
      });

      // 2) The CRM draft keeps only the harmless details and a "saved"
      // mark. deleteField() also removes any old password left in a draft.
      await setDoc(doc(db, "societies", societyId, "settings", "init"), {
        bankSync: {
          method: "imap",
          provider: bankSync.provider,
          host: bankSync.provider === "other" ? bankSync.host.trim() : null,
          email: bankSync.email.trim(),
          mailboxFolder: bankSync.mailboxFolder.trim(),
          passwordSaved: true,
          appPassword: deleteField(),
        },
      }, { merge: true });

      // 3) Forget the password in the browser too.
      setBankSync(prev => ({ ...prev, appPassword: "", passwordSaved: true }));

      setStep(4);
    } catch (err) {
      console.error("Bank email save failed:", err);
      setSaveError("Couldn't save: " + (err.message || "Unknown error. Check your connection."));
    } finally {
      setSaving(false);
    }
  }

  // ── Step 2 → writes every flat under the society created in Step 1 ──
  const DEFAULT_OWNER_PASSWORD = "Welcome@123"; // owner is forced to change this on first login

  // ── Step 4 → writes every flat (and, where MC Role is set on a row,
  // that row's roles include the MC Role — the flat's legal owner IS the
  // MC account). MC size 1's President row gets all three roles, matching
  // the old single-account behaviour. ──
  async function saveFlatsStep() {
    setSaving(true);
    setSaveError("");
    setStep2Progress("");
    try {
      const occupiedRate = Number(society.occupiedRate);
      let vacantRate = 0;
      if (society.vacantMethod === "fixed") vacantRate = Number(society.vacantRate);
      else if (society.vacantMethod === "percentage")
        vacantRate = Math.round(occupiedRate * Number(society.vacantPct) / 100);

      // Pass 1 (reads) — figure out, per mobile number, whether an account
      // already exists for this society (skip re-creating) or this is a
      // second flat for someone who already got a row earlier in this same
      // sheet (append flatIds instead of creating a duplicate account).
      const existingByMobile = {}; // mobile -> { uid, isNew, flatIds: [] }
      const validRows = flats.filter(f => f.flatNo.trim() && isValidMobile(f.mobile1));

      for (let i = 0; i < validRows.length; i++) {
        const f = validRows[i];
        const mobile = f.mobile1;
        setStep2Progress(`Checking ${i + 1} of ${validRows.length}…`);

        if (existingByMobile[mobile]) {
          if (!existingByMobile[mobile].flatIds.includes(f.flatNo.trim())) existingByMobile[mobile].flatIds.push(f.flatNo.trim());
          continue;
        }

        const existing = await getDocs(query(collection(db, "users"), where("mobile", "==", mobile)));
        const match = existing.docs.find(d => (d.data().societies || []).includes(societyId));

        if (match) {
          // Already has an account in this society — find their existing
          // flatIds so we can append, not overwrite.
          const memberSnap = await getDocs(
            query(collection(db, "societies", societyId, "members"), where("mobile", "==", mobile))
          );
          const prevFlatIds = memberSnap.empty ? [] : (memberSnap.docs[0].data().flatIds || []);
          existingByMobile[mobile] = { uid: match.id, isNew: false, flatIds: Array.from(new Set([...prevFlatIds, f.flatNo.trim()])) };
        } else {
          existingByMobile[mobile] = { uid: doc(collection(db, "users")).id, isNew: true, flatIds: [f.flatNo.trim()] };
        }
      }

      // Pass 2 (writes) — batched in chunks of 400 (Firestore's batch limit is 500)
      const hash = await bcrypt.hash(DEFAULT_OWNER_PASSWORD, 10);
      let batch = writeBatch(db);
      let opsInBatch = 0;
      const commitIfFull = async () => {
        if (opsInBatch >= 400) {
          await batch.commit();
          batch = writeBatch(db);
          opsInBatch = 0;
        }
      };

      for (let i = 0; i < flats.length; i++) {
        const f = flats[i];
        const flatNo = f.flatNo.trim();
        if (!flatNo) continue;
        setStep2Progress(`Saving ${i + 1} of ${flats.length}…`);

        batch.set(doc(db, "societies", societyId, "flats", flatNo), {
          flatNo,
          block: f.block.trim(),
          floor: normalizeFloor(f.floor) ?? f.floor.trim(),
          // Field key is legalOwnerName (uniform with FlatsScreen.jsx read key).
          legalOwnerName: f.legalOwnerName.trim() || "",
          representativeName: f.representativeName.trim() || "",
          representative: f.representativeName.trim() || "",
          flatType: f.flatType.trim() || "",
          sqFt: Number(f.sqFt) || 0,
          doorNo: f.doorNo.trim() || "",
          // FIX (field-name mismatch): FlatsScreen's Legal tab reads
          // ptinNo/serviceNo/uscNo, not these longer names — renamed to
          // match, since nothing else in the app reads the old keys.
          ptinNo: f.propertyTaxNo.trim() || "",
          serviceNo: f.electricityServiceNo.trim() || "",
          uscNo: f.electricityUscNo.trim() || "",
          mobile1: f.mobile1 || "",
          mobile2: f.mobile2 || "",
          emailId: (f.emailId || "").trim(),
          status: f.status,
          mcRole: f.mcRole || "",
          ...(String(f.openingArrearsMonths ?? "").trim() !== "" && !Number.isNaN(Number(f.openingArrearsMonths)) ? { openingArrearsMonths: Number(f.openingArrearsMonths) } : {}),
          maintenanceRate: Number(f.rate) || (f.status === "Occupied" ? occupiedRate : vacantRate),
          // FlatsScreen.jsx (saas-app) sorts by this number, falling back to
          // alphabetical flatNo order if missing — which gets "10" before
          // "2" wrong. Spaced by 10s (not i+1) so a flat can be inserted
          // between two existing ones later without renumbering everything.
          sortOrder: (i + 1) * 10,
        });
        opsInBatch++;
        await commitIfFull();
      }

      for (const mobile of Object.keys(existingByMobile)) {
        const entry = existingByMobile[mobile];
        const f = validRows.find(r => r.mobile1 === mobile);

        if (entry.isNew) {
          batch.set(doc(db, "users", entry.uid), {
            mobile,
            password: hash,
            name: f.legalOwnerName.trim() || entry.flatIds[0],
            emailId: (f.emailId || "").trim(),
            societies: [societyId],
            active: true,
            failedAttempts: 0,
            lockedUntil: null,
            autoLockAt: null,
            mustChangePassword: true, // forces password change on first login
            createdAt: new Date().toISOString(),
          });
          opsInBatch++;
          await commitIfFull();
        }

        // MC size 1's President row runs the whole committee, same as the
        // old single-account behaviour — gets all three roles.
        const mcRole = (f.mcRole || "").trim();
        const roles = !mcRole
          ? ["FlatOwner"]
          : (customer.mcMemberCount === "1"
              ? ["FlatOwner", "President", "Secretary", "Treasurer"]
              : ["FlatOwner", mcRole]);

        batch.set(doc(db, "societies", societyId, "members", entry.uid), {
          name: f.legalOwnerName.trim() || entry.flatIds[0],
          mobile,
          emailId: (f.emailId || "").trim(),
          roles,
          flatIds: entry.flatIds, // array — supports owning more than one flat
          memberType: "owner",
          paysMaintenance: true,
          paysWater: true,
        });
        opsInBatch++;
        await commitIfFull();
      }

      if (opsInBatch > 0) await batch.commit();

      setStep2Progress("");
      setStep(5);
    } catch (err) {
      console.error("Flats save failed:", err);
      setSaveError("Couldn't save flats: " + (err.message || "Unknown error. Check your connection."));
    } finally {
      setSaving(false);
    }
  }

  // ── New Step 5 → saves the customer's agreed pricing to settings/init.
  // For the "custom" case (above the top tier), there's nothing to agree
  // to yet — just move on, no charge is set until a manual conversation.
  async function savePricing(match) {
    setSaving(true);
    setSaveError("");
    try {
      const pricingRecord = match.type === "tier"
        ? { type: "tier", label: match.label, priceMonthly: match.priceMonthly, agreedAt: new Date().toISOString() }
        : { type: "custom", message: match.message };
      await setDoc(doc(db, "societies", societyId, "settings", "init"), {
        pricing: pricingRecord,
      }, { merge: true });

      setAgreedPricing(match.type === "tier" ? match : null);
      setStep(6);
    } catch (err) {
      console.error("Pricing save failed:", err);
      setSaveError("Couldn't save: " + (err.message || "Unknown error. Check your connection."));
    } finally {
      setSaving(false);
    }
  }

  // ── Final Confirm → everything's already saved; this just activates
  // the trial (status "draft" → "trial", starts the 45-day countdown) ──
  async function handleConfirm() {
    setSaving(true);
    setSaveError("");
    try {
      const trialStartedAt = new Date(); // clean "join date" — set once, at Confirm
      const trialEndsAt = new Date(trialStartedAt.getTime() + TRIAL_DAYS * 24 * 60 * 60 * 1000);
      // Billing year is anchored to the day after the trial ends, not to any
      // separately-tracked "actual paid" date — see Section 7 of the audit.
      const subscriptionStartDate = new Date(trialEndsAt.getTime() + 24 * 60 * 60 * 1000);
      const subscriptionEndDate = new Date(subscriptionStartDate.getTime() + 365 * 24 * 60 * 60 * 1000);
      await setDoc(doc(db, "societies", societyId), {
        status: "trial",
        trialStartedAt: trialStartedAt.toISOString(),
        trialEndsAt: trialEndsAt.toISOString(),
        subscriptionStartDate: subscriptionStartDate.toISOString(),
        subscriptionEndDate: subscriptionEndDate.toISOString(),
      }, { merge: true });

      // Only now — everything is finished and correct — does saas-app
      // (the real customer database) ever see this society. This also
      // creates each member's real login account (see pushSocietyToSaasApp).
      const { failures, manualHandoffs, bills } = await pushSocietyToSaasApp(societyId, setConfirmProgress);

      const messages = [];
      if (manualHandoffs.length > 0) {
        messages.push(`${manualHandoffs.length} member(s) need their password relayed by hand (no email on file):\n` + manualHandoffs.join("\n"));
      }
      if (failures.length > 0) {
        messages.push(`${failures.length} account(s) need attention:\n` + failures.join("\n"));
      }
      // The society and every successful account are live. Nothing is closed here: the last
      // screen (DoneStep) shows the bills and any follow-up, and its Done button calls
      // onComplete, so a warning can no longer vanish before it is read.
      autoDownloadTerms();
      setDoneInfo({ bills, messages, handoffCount: manualHandoffs.length });
      setFlowStage("done");
    } catch (err) {
      console.error("Confirm failed:", err);
      setSaveError("Setup failed: " + (err.message || "Unknown error. Check your connection."));
    } finally {
      setSaving(false);
    }
  }

  // ── Global "Step X of Y" — covers every screen from Welcome Tour
  // screen 1 through the final Review, in order. Stage 4 (cost/terms)
  // isn't included since it isn't built yet (see design doc). ──
  const TOTAL_SCREENS = 17; // 6 welcome + 2 customerIntro + 2 features + 7 wizard (Bank Email skipped for manual-entry societies)
  function globalStepNumber() {
    if (flowStage === "welcome") return welcomeScreen + 1; // 1–6
    if (flowStage === "customerIntro") {
      return 6 + { info: 1, mcSize: 2 }[introScreen]; // 7–8
    }
    if (flowStage === "features") {
      return 8 + { mandatory: 1, optional: 2 }[featuresScreen]; // 9–10
    }
    if (flowStage === "wizard") return 10 + step + 1; // 11–17
    return null;
  }

  return (
    <div style={S.page}>
      {/* Brand */}
      <div style={S.brand}>
        <img src={adyantamLogo} alt="Adyantam" style={S.logo} />
        <div style={S.brandName}>Adyantam Building Circle®</div>
        <div style={S.brandSub}>First-Time Setup</div>
      </div>

      {globalStepNumber() && flowStage !== "wizard" && (
        <div style={{ fontSize: "11px", color: "#8888a8", textAlign: "center", marginBottom: "10px" }}>
          Step {globalStepNumber()} of {TOTAL_SCREENS}
        </div>
      )}

      {flowStage === "welcome" && (
        <WelcomeTour screen={welcomeScreen} setScreen={setWelcomeScreen} onFinish={() => setFlowStage("customerIntro")}
          onResumeClick={() => setFlowStage("resume")} />
      )}

      {flowStage === "resume" && (
        <ResumeSetup onResume={applyResumedDraft} onBack={() => setFlowStage("welcome")} />
      )}

      {flowStage === "customerIntro" && introScreen === "info" && (
        <CustomerIntroInfo
          onNext={() => setIntroScreen("mcSize")}
          onBack={() => { setWelcomeScreen(WELCOME_THEMES.length - 1); setFlowStage("welcome"); }}
        />
      )}

      {flowStage === "customerIntro" && introScreen === "mcSize" && (
        <McSizeScreen
          mcMemberCount={customer.mcMemberCount}
          onMcCountChange={updateMcMemberCount}
          onNext={saveCustomerIntro}
          onBack={() => setIntroScreen("info")}
          saving={customerSaving}
        />
      )}

      {flowStage === "features" && featuresScreen === "mandatory" && (
        <FeatureSelectionMandatory
          onNext={() => setFeaturesScreen("optional")}
          onBack={() => { setIntroScreen("mcSize"); setFlowStage("customerIntro"); }}
        />
      )}

      {flowStage === "features" && featuresScreen === "optional" && (
        <FeatureSelectionOptional
          enabled={featuresEnabled}
          onToggle={toggleFeature}
          waterMode={waterBillingMode}
          onWaterMode={setWaterBillingMode}
          waterApprovalFlow={society.waterApprovalFlow}
          onWaterApprovalFlow={(v) => updateSociety("waterApprovalFlow", v)}
          onNext={() => setFlowStage("wizard")} // Stage 4 (cost/terms) isn't designed yet — see design doc
          onBack={() => setFeaturesScreen("mandatory")}
        />
      )}

      {flowStage === "done" && doneInfo && (
        <DoneStep societyId={societyId} bills={doneInfo.bills} messages={doneInfo.messages} handoffCount={doneInfo.handoffCount} onDone={() => onComplete(societyId)} />
      )}

      {flowStage === "wizard" && (
        <>
          <Progress step={step} globalStep={globalStepNumber()} totalScreens={TOTAL_SCREENS} />
          {societyId && (
            <div style={{ textAlign: "center", fontSize: "11px", color: "#555570", marginTop: "-12px", marginBottom: "16px" }}>
              Setup ID: <strong style={{ color: "#8888a8" }}>{societyId}</strong> — save this to resume later
            </div>
          )}

          {saveError && (
            <div style={{ ...S.error, width: "100%", maxWidth: "480px", marginBottom: "12px", whiteSpace: "pre-line" }}>
              ⚠ {saveError}
            </div>
          )}

          {step === 0 && (
            <Step1Identity data={society} onChange={updateSociety}
              onNext={saveStep1Identity} onBack={() => setFlowStage("features")} saving={saving} />
          )}
          {step === 1 && (
            <Step1Billing data={society} onChange={updateSociety}
              onNext={saveStep1Billing} onBack={() => setStep(0)} saving={saving} />
          )}
          {step === 2 && (
            <ReconciliationStep value={reconciliationMethod} onChange={setReconciliationMethod}
              onNext={saveReconciliation} onBack={() => setStep(1)} saving={saving} />
          )}
          {step === 3 && (
            <BankEmailStep data={bankSync} onChange={updateBankSync}
              onNext={saveBankEmail} onBack={() => setStep(2)} saving={saving} />
          )}
          {step === 4 && (
            <Step2 society={society} flats={flats} setFlats={setFlats} mcMemberCount={customer.mcMemberCount}
              onNext={saveFlatsStep} onBack={() => setStep(reconciliationMethod === "manual" ? 2 : 3)} saving={saving} progressText={step2Progress} />
          )}
          {step === 5 && (
            <PricingStep society={society} onNext={savePricing} onBack={() => setStep(4)} saving={saving}
              agreed={pricingAgreed} setAgreed={setPricingAgreed}
              pricing={pricing} pricingLoading={pricingLoading} pricingError={pricingError} />
          )}
          {step === 6 && (
            <Step4 society={society} flats={flats} bankSync={bankSync} agreedPricing={agreedPricing}
              reconciliationMethod={reconciliationMethod}
              onBack={() => setStep(5)} onConfirm={handleConfirm} saving={saving} progressText={confirmProgress} />
          )}
        </>
      )}

      <div style={{ fontSize: "10px", color: "#6666a0", marginTop: "20px", textAlign: "center", lineHeight: "1.7", maxWidth: "480px" }}>
        <div style={{ fontWeight: "600", color: "#8888a8", marginBottom: "3px" }}>Adyantam Building Circle · Empowering Resident Communities</div>
        <div>© 2026 Adyantam. All rights reserved. · v1.0.0</div>
        <div style={{ marginTop: "3px", fontStyle: "italic" }}>you don't need to know code to build — Conceptualised by Dr. Vijay DSK, powered by AI</div>
      </div>
    </div>
  );
}